import { Box, Grid2 as Grid, useMediaQuery, useTheme } from '@mui/material';
import {
  AirVent,
  AlertTriangle,
  Bike,
  Building2,
  Car,
  Caravan,
  ChevronDown,
  Cigarette,
  createLucideIcon,
  Fence,
  FileText,
  Flag,
  Flame,
  Gavel,
  Info,
  Key,
  Leaf,
  Palette,
  PawPrint,
  Refrigerator,
  Satellite,
  Scroll,
  Sparkles,
  Sun,
  Volume2,
  Wallet,
  Waves,
  Zap,
} from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts';

import RadarChart, {
  RadarChartDataPoint,
  RadarChartDataSeries,
} from '@/components/common/RadarChart';
import {
  compareRiskLabelOrder,
  computeConsecutiveMonthsWithoutMeeting,
  type DocumentCitation,
  DocumentCitationRow,
  GOVERNANCE_CADENCE_GAP_THRESHOLD,
  groupRisksByDocument,
  HOA_HERO_ICON_MAP,
  HOA_RISK_ACTOR_LABEL,
  HOAReceivedIcon,
  IssueRow,
  IssueRowUrgency,
  pickHOAHeroFindings,
  resolveRiskActors,
  resolveRiskColor,
  resolveRiskLabel,
  resolveRiskTooltip,
  RESERVE_CRITICAL_PCT,
  RESERVE_HEALTHY_TARGET_PCT,
  ScrollHintList,
  type HOARiskActor,
  Tip,
  useOpenDocumentRecord,
} from '@/components/dashboard/scoring';
import { AskHauserList } from '@/components/dashboard/scoring/AskHauserList';
import { COLORS } from '@/components/dashboard/scoring/constants';
import DocFolder from '@/components/home/cards/DocFolder';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHOAChecklist } from '@/hooks/useHOAChecklist';
import {
  buildHOADueDiligenceQuestions,
  HOA_DUE_DILIGENCE_TOPICS,
  type HOADueDiligenceTopic,
} from '@/pages/dashboard/hoaDueDiligenceQuestions';
import { VALUE_FONT } from '@/theme/consts';
import {
  ChecklistItem,
  getHOADisclosureTier,
  HOADisclosureTier,
  HOAAnnualBudgetFindings,
  HOAChecklistResponse,
  HOAEnvironmentalFindings,
  HOAEnvironmentalRiskFinding,
  HOABoardMeetingEntry,
  HOABoardMinutesFindings,
  HOAFindings,
  HOAInsuranceSummary,
  HOAChecklistSource,
  HOALitigationMatter,
  HOAOperatingRulesFindings,
  HOASpecialAssessment,
  HOATransferFindings,
  HOAViolation,
} from '@/types/HOA';

// Same taxonomy ConditionScoreView uses for inspection issues (colors,
// labels, the whole IssueRow chip) — mapped from the 4-level environmental
// severity scale instead of invented fresh. "informational" reads as
// "no action needed," which is what Monitor means in the condition context.
const ENV_SEVERITY_TO_URGENCY: Record<HOAEnvironmentalRiskFinding['severity'], IssueRowUrgency> = {
  critical: 'Critical',
  high: 'Urgent',
  moderate: 'Moderate',
  informational: 'Monitor',
};

const ENV_SEVERITY_ORDER: Record<HOAEnvironmentalRiskFinding['severity'], number> = {
  critical: 0,
  high: 1,
  moderate: 2,
  informational: 3,
};

// Donut mix for EnvironmentalRiskModule — broken down by the same resolved
// label each risk row shows (resolveRiskLabel), not by raw severity. Raw
// Critical/Urgent/Moderate is exactly the flattening this taxonomy exists
// to get away from: two findings can share a severity (e.g. both
// "critical") while meaning completely different things — a recorded
// legal covenant vs. three broken HOA-maintained exhaust fans — so the
// donut's own breakdown needs to speak the same label system as the list
// below it, not fall back to the old scale.
interface RiskLabelDonutSlice {
  key: string;
  name: string;
  value: number;
  color: string;
}

const HOA_SECTION_HEADING_STYLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: COLORS.accent2,
  fontFamily: "'Manrope', sans-serif",
};

function HOAContentSection({
  title,
  children,
  gap = '20px',
}: {
  title: string;
  children: React.ReactNode;
  gap?: string;
}) {
  return (
    <Box
      component="section"
      aria-label={title}
      sx={{ display: 'flex', flexDirection: 'column', gap }}>
      {children}
    </Box>
  );
}

function buildRiskLabelDonutData(risks: HOAEnvironmentalRiskFinding[]): RiskLabelDonutSlice[] {
  const byLabel = new Map<string, number>();
  for (const risk of risks) {
    const label = resolveRiskLabel(risk);
    byLabel.set(label, (byLabel.get(label) ?? 0) + 1);
  }
  return [...byLabel.entries()]
    .sort(([a], [b]) => compareRiskLabelOrder(a, b))
    .map(([label, count]) => ({
      key: label,
      name: label,
      value: count,
      color: resolveRiskColor(label),
    }));
}

// Compact severity-mix donut, not the full interactive pop-out chart
// SubscoreDetailCard uses (blur ring, hover popover) — this just needs to
// break up the card's text density with a quick "how bad is it" visual and
// a legend, not a whole exploratory tool.
function RiskSeverityDonut({ risks }: { risks: HOAEnvironmentalRiskFinding[] }) {
  const data = buildRiskLabelDonutData(risks);
  const total = risks.length;
  if (total === 0) return null;
  const size = 230;

  return (
    <div
      style={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        WebkitTapHighlightColor: 'transparent',
      }}>
      {/* recharts sectors are SVG paths with click handlers — some browsers
          give them a default focus/tap highlight (a blue outline or ring)
          like any other clickable element. Suppress it; the donut has its
          own hover tooltip already. */}
      <style>{`
        .recharts-wrapper, .recharts-wrapper *, .recharts-sector {
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          pointerEvents: 'none',
        }}>
        <span
          style={{
            fontFamily: VALUE_FONT,
            fontSize: 48,
            fontWeight: 200,
            letterSpacing: '-0.07em',
            color: COLORS.text,
            lineHeight: 1,
          }}>
          {total}
        </span>
        <span
          style={{
            ...HOA_SECTION_HEADING_STYLE,
            marginTop: 6,
          }}>
          {total === 1 ? 'Finding' : 'Findings'}
        </span>
      </div>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            startAngle={90}
            endAngle={-270}
            innerRadius={104}
            outerRadius={112}
            paddingAngle={1.5}
            cornerRadius={2}
            dataKey="value">
            {data.map((slice) => (
              <Cell key={slice.key} fill={slice.color} stroke="none" />
            ))}
          </Pie>
          <ReTooltip
            cursor={{ fill: 'transparent' }}
            isAnimationActive={false}
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const slice = payload[0].payload as RiskLabelDonutSlice;
              return (
                <div
                  style={{
                    background: 'rgba(var(--tx-ink-rgb), 0.05)',
                    borderRadius: '9px',
                    padding: 12,
                    fontSize: 11,
                    fontFamily: "'Manrope', sans-serif",
                    color: COLORS.text,
                    maxWidth: 240,
                    lineHeight: 1.45,
                  }}>
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: 10,
                      fontWeight: 600,
                      background: `${slice.color}1a`,
                      color: slice.color,
                      borderRadius: '6px',
                      padding: '3px 10px',
                      whiteSpace: 'nowrap',
                      fontFamily: "'Manrope', sans-serif",
                    }}>
                    {slice.name}: {slice.value}
                  </span>
                  <div style={{ marginTop: 6, color: COLORS.textDim }}>
                    {resolveRiskTooltip(slice.name)}
                  </div>
                </div>
              );
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

const formatRiskCategoryLabel = (category: string): string =>
  category.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Civil Code §5305: HOAs with gross annual income/expenses over this amount
// must have an independent review of their financial statement. Confirmed
// against the real Annual Budget Report, which cites this exact threshold.
const FINANCIAL_REVIEW_INCOME_THRESHOLD = 75000;
const FINANCIAL_STATEMENT_ITEM_ID = 'financial-statement';

const fmtMoney = (n: number | null | undefined): string => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return 'Not disclosed';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const fmtAssessmentFrequency = (
  frequency: HOAFindings['assessmentFrequency'] | string | null | undefined,
): string | undefined => {
  if (typeof frequency !== 'string' || frequency.toLowerCase() === 'null') return undefined;
  return frequency;
};

const fmtDate = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

function citationsForRecordIds(ids: Array<string | undefined>, label: string): DocumentCitation[] {
  const uniqueIds = [...new Set(ids.filter((id): id is string => !!id))];
  return uniqueIds.map((documentRecordId, index) => ({
    documentRecordId,
    label: uniqueIds.length > 1 ? `${label} ${index + 1}` : label,
  }));
}

function mergeCitations(citations: DocumentCitation[]): DocumentCitation[] {
  const byId = new Map<string, DocumentCitation>();
  for (const citation of citations) {
    const identity = citation.manifestItemId ?? citation.documentRecordId;
    const existing = byId.get(identity);
    if (!existing) {
      byId.set(identity, { ...citation });
    } else if (!existing.label.split(' / ').includes(citation.label)) {
      existing.label = `${existing.label} / ${citation.label}`;
    }
  }
  return [...byId.values()];
}

function citationsForLogicalDocuments(
  documents: HOAChecklistSource[] | undefined,
  classifications: string[],
  fallback: DocumentCitation[],
): DocumentCitation[] {
  const matchingDocuments = (documents ?? []).filter(
    (document) => document.classification && classifications.includes(document.classification),
  );
  if (matchingDocuments.length === 0) return fallback;

  return matchingDocuments.map((document) => ({
    documentRecordId: document.documentRecordId,
    manifestItemId: document.manifestItemId,
    primaryPage: document.primaryPage,
    label: document.title,
  }));
}

function ageRestrictionCitations(
  findings: HOAOperatingRulesFindings,
  documents: HOAChecklistSource[] | undefined,
): DocumentCitation[] {
  const explicitAgeDocuments = citationsForLogicalDocuments(documents, ['HOA_AGE_RESTRICTION'], []);
  if (explicitAgeDocuments.length > 0) return explicitAgeDocuments;

  const sourceIds = new Set(
    (findings.documentRecordIds ?? [findings.documentRecordId]).filter((id): id is string =>
      Boolean(id),
    ),
  );
  const relevantLogicalDocuments = (documents ?? []).filter(
    (document) =>
      sourceIds.has(document.documentRecordId) &&
      ['HOA_CCRS', 'HOA_OPERATING_RULES'].includes(document.classification ?? ''),
  );
  if (relevantLogicalDocuments.length > 0) {
    return relevantLogicalDocuments.map((document) => ({
      documentRecordId: document.documentRecordId,
      manifestItemId: document.manifestItemId,
      primaryPage: document.primaryPage,
      label: document.title,
    }));
  }

  return citationsForRecordIds([...sourceIds], 'Age Restriction Source');
}

function environmentalRiskCitations(
  risks: HOAEnvironmentalRiskFinding[],
  fallbackDocumentRecordId?: string,
): DocumentCitation[] {
  const citations = risks.flatMap((risk) => {
    if (risk.logicalSources?.length) {
      return risk.logicalSources.map((source) => ({
        documentRecordId: source.documentRecordId,
        manifestItemId: source.manifestItemId,
        primaryPage: source.primaryPage,
        label: source.title,
      }));
    }
    const label =
      risk.sourceDocumentType === 'HOA_ENVIRONMENTAL_REPORT'
        ? 'Environmental Monitoring Report'
        : risk.sourceDocumentType === 'HOA_CCRS'
        ? 'CC&Rs'
        : groupRisksByDocument([risk])[0]?.label ?? 'Source Document';
    return (risk.documentRecordIds ?? (risk.documentRecordId ? [risk.documentRecordId] : [])).map(
      (documentRecordId) => ({ documentRecordId, label }),
    );
  });
  if (citations.length === 0 && fallbackDocumentRecordId) {
    citations.push({ documentRecordId: fallbackDocumentRecordId, label: 'CC&Rs' });
  }
  return mergeCitations(citations);
}

/**
 * HOA due-diligence detail — a montage of purpose-built modules borrowed
 * from patterns already used across the app, not one bespoke layout:
 *   - Snapshot: big-number completion + hero finding (HomeScoreCard's
 *     big-number treatment + HOAChecklistCard's hero, mirroring NHD).
 *   - Financial Summary: stat cards (NHD's Tax Profile pattern).
 *   - Risk & Compliance: flagged/prioritized rows (Repair Planner pattern).
 *   - Governance Documents: California transfer-disclosure taxonomy,
 *     separated into required, conditional, requested, and supplemental.
 * As more document categories get their own extractors, each gets
 * its own module in this montage rather than folding into one generic list.
 */
export const HOADetailView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { selectedProperty } = usePropertiesContext();
  const propertyId = selectedProperty?.property.id;
  const { data, isLoading } = useHOAChecklist(propertyId);
  // Big screens (27"-class) split the page into a fixed instrument column (the snapshot and the HOA radar) and a
  // reading column of the detail cards, so the eye moves down one column instead of across the whole width
  const fitsWide = () => window.innerWidth >= 1680 && window.innerHeight >= 1000;
  const [isWide, setIsWide] = useState(fitsWide);
  useEffect(() => {
    const onResize = () => setIsWide(fitsWide());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (isLoading || !data) {
    return (
      <Box
        sx={{
          pt: isMobile ? '10px' : '20px',
          px: isMobile ? '10px' : 0,
          mx: 'auto',
        }}>
        <div className="hx-on-ground" style={{ fontSize: 13, color: COLORS.textDim, padding: '40px 0' }}>
          {isLoading ? 'Loading…' : "This property isn't marked as HOA-governed."}
        </div>
      </Box>
    );
  }

  const heroFindings = pickHOAHeroFindings(
    data.hoaFindings,
    data.hoaBoardMinutesFindings,
    data.hoaEnvironmentalFindings,
    data.hoaAnnualBudgetFindings,
  );
  // The same checklist powers the required-disclosure headline and the
  // tiered Governance Documents detail. Conditional/requested categories
  // remain visible without being counted as missing required disclosures.
  const governanceItems = data.hoaChecklist;
  const governanceDocumentRecordIds = buildChecklistDocumentRecordIds(data);
  const financialStatementItem = data.hoaChecklist.find(
    (c) => c.id === FINANCIAL_STATEMENT_ITEM_ID,
  );
  const financialReviewGap =
    financialStatementItem?.status === 'NOT_RECEIVED' &&
    (data.hoaAnnualBudgetFindings?.totalAnnualIncome ?? 0) > FINANCIAL_REVIEW_INCOME_THRESHOLD;
  const governanceExtractionSources = [
    { label: 'Demand Statement', extractedAt: data.hoaFindings?.extractedAt },
    { label: 'Board Minutes', extractedAt: data.hoaBoardMinutesFindings?.extractedAt, monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD },
    { label: 'Operating Rules', extractedAt: data.hoaOperatingRulesFindings?.extractedAt, monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD },
    {
      label: 'Annual Budget / Reserve Study',
      extractedAt: data.hoaAnnualBudgetFindings?.extractedAt,
      monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
    },
    { label: 'CC&Rs', extractedAt: data.hoaEnvironmentalFindings?.extractedAt, monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD },
  ];
  const boardMinutesReceived = data.hoaChecklist.some(
    (item) => item.id === 'board-minutes' && item.status === 'RECEIVED',
  );
  return (
    <Box
      data-testid="hoa-detail-view"
      sx={{
        pt: isMobile ? 0 : '20px',
        pb: isMobile ? '10px' : '20px',
        px: isMobile ? '10px' : 0,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: isMobile ? '10px' : '20px',
      }}>
      {isWide && !isMobile ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: '20px', alignItems: 'start' }}>
          <Box
            sx={{
              // HOA has no property header: the column is the viewport less the frame's 20px gutters, top and bottom,
              // and the HOA card (flex: 1) takes every pixel the snapshot leaves
              position: 'sticky',
              top: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              height: 'calc(100dvh - 40px)',
              minHeight: 760,
              overflowY: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}>
            <SnapshotModule
              checklist={data.hoaChecklist}
              heroFindings={heroFindings}
              operatingRules={data.hoaOperatingRulesFindings}
              documents={data.hoaDocuments}
            />
            <HOAAskHauserModule data={data} stacked />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px', minWidth: 0 }}>
            <FinancialSummaryModule
              findings={data.hoaFindings}
              annualBudget={data.hoaAnnualBudgetFindings}
              transfer={data.hoaTransferFindings}
              documents={data.hoaDocuments}
            />
            <CommunityRulesModule
              findings={data.hoaOperatingRulesFindings}
              environmentalFindings={data.hoaEnvironmentalFindings}
              documents={data.hoaDocuments}
            />
            <RiskComplianceModule
              findings={data.hoaFindings}
              annualBudget={data.hoaAnnualBudgetFindings}
              boardMinutes={data.hoaBoardMinutesFindings}
              boardMinutesReceived={boardMinutesReceived}
              documents={data.hoaDocuments}
            />
            <EnvironmentalRiskModule findings={data.hoaEnvironmentalFindings} />
            <GovernanceDocumentsModule
              items={governanceItems}
              financialReviewGap={financialReviewGap}
              documentRecordIds={governanceDocumentRecordIds}
              extractionSources={governanceExtractionSources}
            />
          </Box>
        </Box>
      ) : (
        <>
      <SnapshotModule
        checklist={data.hoaChecklist}
        heroFindings={heroFindings}
        operatingRules={data.hoaOperatingRulesFindings}
        documents={data.hoaDocuments}
      />
      <HOAAskHauserModule data={data} />

      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <HOAContentSection title="Costs & Ownership" gap="10px">
            <FinancialSummaryModule
              findings={data.hoaFindings}
              annualBudget={data.hoaAnnualBudgetFindings}
              transfer={data.hoaTransferFindings}
              documents={data.hoaDocuments}
            />
            <CommunityRulesModule
              findings={data.hoaOperatingRulesFindings}
              environmentalFindings={data.hoaEnvironmentalFindings}
              documents={data.hoaDocuments}
            />
          </HOAContentSection>
          <HOAContentSection title="Risk & Governance" gap="10px">
            <RiskComplianceModule
              findings={data.hoaFindings}
              annualBudget={data.hoaAnnualBudgetFindings}
              boardMinutes={data.hoaBoardMinutesFindings}
              boardMinutesReceived={boardMinutesReceived}
              documents={data.hoaDocuments}
            />
            <EnvironmentalRiskModule findings={data.hoaEnvironmentalFindings} />
          </HOAContentSection>
          <HOAContentSection title="Disclosure completeness" gap="10px">
            <GovernanceDocumentsModule
              items={governanceItems}
              financialReviewGap={financialReviewGap}
              documentRecordIds={governanceDocumentRecordIds}
              extractionSources={[
                { label: 'Demand Statement', extractedAt: data.hoaFindings?.extractedAt },
                {
                  label: 'Board Minutes',
                  extractedAt: data.hoaBoardMinutesFindings?.extractedAt,
                  monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
                },
                {
                  label: 'Operating Rules',
                  extractedAt: data.hoaOperatingRulesFindings?.extractedAt,
                  monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
                },
                {
                  label: 'Annual Budget / Reserve Study',
                  extractedAt: data.hoaAnnualBudgetFindings?.extractedAt,
                  monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
                },
                {
                  label: 'CC&Rs',
                  extractedAt: data.hoaEnvironmentalFindings?.extractedAt,
                  monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
                },
              ]}
            />
          </HOAContentSection>
        </Box>
      ) : (
        <>
          <HOAContentSection title="Costs & Ownership">
            <Grid container spacing="20px">
              <Grid size={{ sm: 12, md: 6 }} sx={{ minWidth: 0 }}>
                <FinancialSummaryModule
                  findings={data.hoaFindings}
                  annualBudget={data.hoaAnnualBudgetFindings}
                  transfer={data.hoaTransferFindings}
                  documents={data.hoaDocuments}
                />
              </Grid>
              <Grid size={{ sm: 12, md: 6 }} sx={{ minWidth: 0 }}>
                <CommunityRulesModule
                  findings={data.hoaOperatingRulesFindings}
                  environmentalFindings={data.hoaEnvironmentalFindings}
                  documents={data.hoaDocuments}
                />
              </Grid>
            </Grid>
          </HOAContentSection>
          <HOAContentSection title="Risk & Governance">
            <RiskComplianceModule
              findings={data.hoaFindings}
              annualBudget={data.hoaAnnualBudgetFindings}
              boardMinutes={data.hoaBoardMinutesFindings}
              boardMinutesReceived={boardMinutesReceived}
              documents={data.hoaDocuments}
            />
            <EnvironmentalRiskModule findings={data.hoaEnvironmentalFindings} />
          </HOAContentSection>
          <HOAContentSection title="Disclosure completeness">
            <GovernanceDocumentsModule
              items={governanceItems}
              financialReviewGap={financialReviewGap}
              documentRecordIds={governanceDocumentRecordIds}
              extractionSources={[
                { label: 'Demand Statement', extractedAt: data.hoaFindings?.extractedAt },
                {
                  label: 'Board Minutes',
                  extractedAt: data.hoaBoardMinutesFindings?.extractedAt,
                  monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
                },
                {
                  label: 'Operating Rules',
                  extractedAt: data.hoaOperatingRulesFindings?.extractedAt,
                  monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
                },
                {
                  label: 'Annual Budget / Reserve Study',
                  extractedAt: data.hoaAnnualBudgetFindings?.extractedAt,
                  monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
                },
                {
                  label: 'CC&Rs',
                  extractedAt: data.hoaEnvironmentalFindings?.extractedAt,
                  monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
                },
              ]}
            />
          </HOAContentSection>
        </>
      )}
        </>
      )}
    </Box>
  );
};

const HOME_HQ_CARD_TITLE_STYLE: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 17,
  fontWeight: 400,
  letterSpacing: '-0.015em',
  color: COLORS.text,
  fontFamily: "'Manrope', sans-serif",
  lineHeight: 1.2,
};

function ModuleCard({
  title,
  headerRight,
  headerBottomSpacing = 0,
  onHeaderToggle,
  headerExpanded,
  headerToggleLabel,
  children,
}: {
  title: string;
  headerRight?: React.ReactNode;
  headerBottomSpacing?: number;
  onHeaderToggle?: () => void;
  headerExpanded?: boolean;
  headerToggleLabel?: string;
  children: React.ReactNode;
}) {
  const isHeaderToggle = Boolean(onHeaderToggle);

  return (
    <div
      id={hoaCardAnchor(title)}
      style={{
        background: COLORS.card,
        borderRadius: 25,
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        height: '100%',
        scrollMarginTop: 20,
      }}>
      <div
        role={isHeaderToggle ? 'button' : undefined}
        tabIndex={isHeaderToggle ? 0 : undefined}
        aria-expanded={isHeaderToggle ? headerExpanded : undefined}
        aria-label={isHeaderToggle ? headerToggleLabel : undefined}
        onClick={onHeaderToggle}
        onKeyDown={(event) => {
          if (!isHeaderToggle || (event.key !== 'Enter' && event.key !== ' ')) return;
          event.preventDefault();
          onHeaderToggle?.();
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: headerBottomSpacing,
          cursor: isHeaderToggle ? 'pointer' : undefined,
        }}>
        <span style={HOME_HQ_CARD_TITLE_STYLE}>{title}</span>
        {headerRight}
      </div>
      {children}
    </div>
  );
}

// A card's headerRight caution icon can fire for two structurally different
// reasons, so each gets its own check builder returning a message (or null):
//  - extractionStalenessChecks: the source document hasn't been (re)processed
//    in a while — "this may not reflect your current documents," not a claim
//    about what's inside them. Most findings types have no structured "as
//    of" date for their own content, so extractedAt (when we last processed
//    the document) is the only generically available recency signal.
//  - policyPeriodExpiredCheck: the insurance policy's own stated term has
//    lapsed, per a real policyPeriodEnd field — true content staleness,
//    independent of processing recency (a freshly reprocessed document can
//    still describe an already-expired policy).
// CardCaution renders whichever check fires first, same headerRight/Tip
// treatment as Board Activity's meeting-date staleness badge below, for one
// consistent "something here needs a closer look" convention across cards.
// Default window for the one document type with no natural refresh cadence
// of its own — a Demand Statement is generated fresh per transaction on
// request, not on a fixed schedule, so 6 months since processing is a
// reasonable "this may no longer reflect the current request" heuristic.
const EXTRACTION_STALE_MONTHS_THRESHOLD = 6;

// 12 months for everything with a slower natural cadence — for two
// different reasons that happen to land on the same number:
//  - Board Minutes and the Annual Budget/Reserve Study disclosure are
//    STATUTE-derived: when board minutes are requested, Civil Code §4525
//    specifies the previous 12 months; §5570 requires reserve funding disclosure annually
//    (the underlying reserve study itself only needs a full update every 3
//    years per §5550) — 12 months is the actual required window, not a
//    guess.
//  - CC&Rs and Operating Rules are a HEURISTIC: governing documents amend
//    rarely, usually only via a formal owner vote, so 6 months is too
//    trigger-happy for them; there's no statute setting a specific "must be
//    this recent" window for these, just an estimate of normal cadence.
// This threshold lives on the *document type*, not the card, so the same
// extraction is judged by the same window everywhere it's cited (e.g. CC&Rs
// on both Environmental & Covenant Risks and Governance Documents) —
// otherwise the same document could read as "fine" on one card and "stale"
// on another, the same two-systems problem the document-count and
// insurance-gap fixes both existed to close.
const EXTENDED_STALE_MONTHS_THRESHOLD = 12;

interface CautionCheck {
  message: string;
}

function extractionStalenessChecks(
  sources: Array<{ label: string; extractedAt?: string; monthsThreshold?: number }>,
): CautionCheck[] {
  return sources
    .filter(
      (s): s is { label: string; extractedAt: string; monthsThreshold?: number } => !!s.extractedAt,
    )
    .map((s) => ({
      ...s,
      threshold: s.monthsThreshold ?? EXTRACTION_STALE_MONTHS_THRESHOLD,
      staleMonths: monthsSince(s.extractedAt, new Date()),
    }))
    .filter((s) => s.staleMonths >= s.threshold)
    .sort((a, b) => b.staleMonths - a.staleMonths)
    .map((s) => ({
      message: `${s.label} was last processed ${fmtDate(
        s.extractedAt,
      )} — consider requesting a current copy.`,
    }));
}

function policyPeriodExpiredCheck(policyPeriodEnd: string | undefined): CautionCheck | null {
  if (!policyPeriodEnd) return null;
  const end = new Date(policyPeriodEnd);
  if (Number.isNaN(end.getTime()) || end.getTime() >= Date.now()) return null;
  return {
    message: `Master insurance policy period ended ${fmtDate(
      policyPeriodEnd,
    )} — request a current declarations page.`,
  };
}

function CardCaution({ checks }: { checks: Array<CautionCheck | null> }) {
  const flagged = checks.find((c): c is CautionCheck => !!c);
  if (!flagged) return null;
  return (
    <Tip text={flagged.message}>
      <AlertTriangle size={14} style={{ color: COLORS.urgent, display: 'block' }} />
    </Tip>
  );
}

// Purely informational — explains where the 12-month figure above actually
// comes from, for anyone wondering "says who?" Not a caution (neutral color,
// no urgency), so it never competes with CardCaution for attention when both
// are shown together.
function DavisStirlingInfoTip() {
  return (
    <Tip
      text={
        "California's Davis-Stirling Act (Civil Code §4525–§4530) requires the HOA to give a prospective " +
        'buyer current governing documents plus financial and insurance disclosures. Approved board ' +
        'minutes from the previous 12 months must be provided if the prospective buyer requests them. ' +
        'Reserve funding has its own cadence: a full reserve study at least every 3 years (§5550) plus ' +
        'an annual funding disclosure (§5570).'
      }>
      <Info size={14} style={{ color: COLORS.textDim, display: 'block' }} />
    </Tip>
  );
}

type ActorFilterValue = 'all' | HOARiskActor;

// Same segmented-text-button style as YearFilter (Board Activity) — one
// established convention for "filter this list" controls rather than a new
// visual pattern. Counts are baked into the labels themselves so the
// control doubles as the at-a-glance summary — no separate chart needed
// for "how many are mine vs. the HOA's."
function ActorFilter({
  value,
  onSelect,
  allCount,
  ownerCount,
  hoaCount,
}: {
  value: ActorFilterValue;
  onSelect: (value: ActorFilterValue) => void;
  allCount: number;
  ownerCount: number;
  hoaCount: number;
}) {
  const options: Array<{ key: ActorFilterValue; label: string }> = [
    { key: 'all', label: `All: ${allCount}` },
    { key: 'owner', label: `Owner: ${ownerCount}` },
    { key: 'hoa', label: `HOA: ${hoaCount}` },
  ];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      {options.map((option, i) => {
        const active = option.key === value;
        return (
          <Fragment key={option.key}>
            {i > 0 && <span style={{ fontSize: 11, color: COLORS.accent2 }}>/</span>}
            <button
              type="button"
              onClick={() => onSelect(option.key)}
              style={{
                fontSize: 11,
                fontWeight: active ? 600 : 500,
                padding: 0,
                border: 'none',
                background: 'transparent',
                color: active ? COLORS.text : COLORS.textDim,
                cursor: 'pointer',
                fontFamily: "'Manrope', sans-serif",
                whiteSpace: 'nowrap',
              }}>
              {option.label}
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}

function EnvironmentalRiskModule({ findings }: { findings?: HOAEnvironmentalFindings }) {
  const [expandedRiskKey, setExpandedRiskKey] = useState<string | null>(null);
  const [actorFilter, setActorFilter] = useState<ActorFilterValue>('all');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!findings) return null;
  if (findings.reviewStatus === 'NOT_DETECTED' && findings.risks.length === 0) return null;

  const sortedRisks = [...findings.risks].sort(
    (a, b) => ENV_SEVERITY_ORDER[a.severity] - ENV_SEVERITY_ORDER[b.severity],
  );

  const risksForActor = (actor: HOARiskActor) =>
    findings.risks.filter((risk) => {
      const actors = resolveRiskActors(risk);
      return actors.length === 0 || actors.includes(actor);
    });
  // Counts describe the rows each filter actually displays. Untagged
  // property/title-level findings intentionally remain visible under both
  // actor filters, so they must be included in both displayed totals.
  const ownerCount = risksForActor('owner').length;
  const hoaCount = risksForActor('hoa').length;
  // Property/title-level findings (no actor tag — the covenant, the
  // easements) stay visible under either filter rather than only "All."
  // They're the ones most likely to be the single most important
  // disclosure on the property; a filter that can hide them by accident
  // is worse than no filter.
  const filteredRisks = findings.risks.filter((risk) => {
    if (actorFilter === 'all') return true;
    const actors = resolveRiskActors(risk);
    return actors.length === 0 || actors.includes(actorFilter);
  });

  return (
    <ModuleCard
      title="Environmental & covenant findings"
      headerRight={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            alignSelf: isMobile ? 'flex-end' : undefined,
            gap: 10,
          }}>
          {sortedRisks.length > 0 && (
            <ActorFilter
              value={actorFilter}
              onSelect={setActorFilter}
              allCount={findings.risks.length}
              ownerCount={ownerCount}
              hoaCount={hoaCount}
            />
          )}
          <CardCaution
            checks={extractionStalenessChecks([
              {
                label: 'CC&Rs',
                extractedAt: findings.extractedAt,
                monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
              },
            ])}
          />
        </div>
      }>
      {findings.reviewStatus === 'INDETERMINATE' && (
        <ListRow
          primary="Automated CC&R review was inconclusive"
          secondary="Manual review of the source document is required."
          flagged
          tone="urgent"
        />
      )}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 20 }}>
        {sortedRisks.length > 0 && (
          <div
            style={{
              flex: isMobile ? undefined : '0 0 30%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: isMobile ? 20 : 0,
              paddingBottom: isMobile ? 20 : 0,
            }}>
            <RiskSeverityDonut risks={sortedRisks} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {sortedRisks.length > 0 && (
            <ScrollHintList
              style={{
                maxHeight: 300,
                overflowY: 'auto',
                overflowX: 'hidden',
                // no scrollbar: the list's fading last rows say there's more
                scrollbarWidth: 'none',
              }}>
              {/* Grouped by the recorded document/report each finding traces
                  back to, rather than one flat 17-row list — reads as a
                  coherent packet (covenant, then CC&Rs, then the annual
                  monitoring report) and also keeps near-duplicate findings
                  from different source documents (e.g. the CC&Rs' and the
                  annual report's separate VMS-requirement findings) from
                  reading as redundant, since they visibly sit under two
                  different documents. */}
              {filteredRisks.length === 0 && actorFilter !== 'all' && (
                <EmptyState
                  text={`No findings tagged "${HOA_RISK_ACTOR_LABEL[actorFilter]}" for this property.`}
                />
              )}
              {groupRisksByDocument(filteredRisks).map((group, groupIndex) => {
                const groupRisks = [...group.risks].sort(
                  (a, b) => ENV_SEVERITY_ORDER[a.severity] - ENV_SEVERITY_ORDER[b.severity],
                );
                return (
                  <div key={group.key} style={{ marginTop: groupIndex === 0 ? 0 : 14 }}>
                    <SubLabel>{group.label}</SubLabel>
                    {groupRisks.map((risk, riskIndex) => {
                      // Include source group and occurrence index so duplicate
                      // extracted titles cannot share React identity or
                      // expansion state.
                      const key = `${group.key}-${risk.category}-${risk.title}-${riskIndex}`;
                      const isExpanded = expandedRiskKey === key;
                      const hasObligations = risk.obligations.length > 0;
                      const hasEvidence = risk.evidence.length > 0;
                      const label = resolveRiskLabel(risk);
                      const actors = resolveRiskActors(risk);
                      return (
                        <IssueRow
                          key={key}
                          testId={`environmental-risk-${key}`}
                          bodyTestId={`environmental-risk-${key}-body`}
                          title={risk.title}
                          meta={
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: isMobile ? 'flex-end' : 'center',
                                gap: 6,
                                flexWrap: 'wrap',
                              }}>
                              <span>{formatRiskCategoryLabel(risk.category)}</span>
                              {actors.map((actor) => (
                                <span key={actor} style={{ color: 'var(--tx-faint)' }}>
                                  · {HOA_RISK_ACTOR_LABEL[actor]}
                                </span>
                              ))}
                            </span>
                          }
                          urgency={ENV_SEVERITY_TO_URGENCY[risk.severity]}
                          urgencyLabel={label}
                          urgencyColor={resolveRiskColor(label)}
                          urgencyTooltip={resolveRiskTooltip(label)}
                          expanded={isExpanded}
                          onToggle={() => setExpandedRiskKey(isExpanded ? null : key)}
                          stackedUrgency={isMobile}>
                          <div>{risk.summary}</div>
                          {hasObligations && (
                            <div style={{ marginTop: 8 }}>
                              <SubLabel>Obligations</SubLabel>
                              <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {risk.obligations.map((obligation, i) => (
                                  <li key={i} style={{ marginBottom: 2 }}>
                                    {obligation}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {hasEvidence && (
                            <div
                              style={{
                                marginTop: 8,
                                paddingTop: 8,
                                borderTop: `1px solid ${COLORS.border}`,
                              }}>
                              {risk.evidence.map((evidence, i) => (
                                <div
                                  key={i}
                                  style={{ marginBottom: i < risk.evidence.length - 1 ? 6 : 0 }}>
                                  {evidence.page != null && (
                                    <span
                                      style={{
                                        fontSize: 10,
                                        fontWeight: 600,
                                        color: COLORS.textDim,
                                        marginRight: 6,
                                      }}>
                                      Page {evidence.page}
                                    </span>
                                  )}
                                  <span style={{ fontStyle: 'italic', color: COLORS.textDim }}>
                                    &ldquo;{evidence.excerpt}&rdquo;
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </IssueRow>
                      );
                    })}
                  </div>
                );
              })}
            </ScrollHintList>
          )}
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'flex-end',
          gap: 12,
          marginTop: 'auto',
        }}>
        <div
          style={{
            fontSize: isMobile ? 10 : 11,
            color: COLORS.textDim,
            lineHeight: 1.5,
            textAlign: isMobile ? 'left' : 'right',
            width: isMobile ? '100%' : undefined,
            order: isMobile ? 0 : 1,
          }}>
          <span style={{ fontWeight: 600 }}>Disclaimer: </span>
          Automated issue spotting only—not a legal conclusion. Review the cited CC&R provisions
          with qualified counsel.
        </div>
        <div style={{ order: isMobile ? 1 : 0 }}>
          <DocumentCitationRow
            citations={environmentalRiskCitations(filteredRisks, findings.documentRecordId)}
            pushToBottom={false}
          />
        </div>
      </div>
    </ModuleCard>
  );
}

// ─── HOA risk radar — a light, client-computed overview across 5 due-
// diligence dimensions. Deliberately NOT the Condition/Context Score
// pattern: no backend computation, no persisted value, no versioned rubric.
// Each axis is a deterministic "worst signal wins" read of data the page
// already has in hand (same philosophy as hoaRiskRegistry.ts's category
// resolution), recomputed fresh on every render. If this ever needs history
// or reactive alerting, that requires server-side computation at ingestion
// time — nothing here — but the RadarChart component itself is agnostic to
// where its data comes from, so swapping in a backend-computed source later
// is a data-source change, not a rebuild.
type HOARiskTier = 'critical' | 'high' | 'moderate' | 'low' | 'unreviewed';

// Radial position must mean the same thing on every radar chart in the app:
// pushed out toward the edge = good (Condition/Context Score both plot
// higher score = further out). So the BEST tier gets the highest value here,
// same as everywhere else — a risk radar spiking outward would read as
// "great shape" when it actually means "maximum risk," a direct contradiction
// of the existing convention. Color (below) still encodes severity directly,
// independent of position.
const HOA_RISK_TIER_VALUE: Record<HOARiskTier, number> = {
  low: 100,
  moderate: 75,
  high: 50,
  critical: 25,
  unreviewed: 0,
};

// Same severity palette hoaRiskRegistry.ts's resolveRiskColor draws from —
// one shared "how alarming is this" color vocabulary across the HOA page.
const HOA_RISK_TIER_COLOR: Record<HOARiskTier, string> = {
  critical: COLORS.critical,
  high: COLORS.urgent,
  moderate: COLORS.moderate,
  low: COLORS.low,
  unreviewed: COLORS.monitor,
};

const HOA_RISK_RADAR_TOOLTIPS: Record<string, string> = {
  FINANCIAL: 'Reserve funding, assessments, delinquencies, and other financial obligations.',
  LEGAL: 'Open violations, litigation, and other legal matters affecting the property or HOA.',
  SITE: 'Site and environmental conditions, recorded covenants, monitoring duties, and related property exposure.',
  GOVERNANCE: 'Board oversight, operating restrictions, meeting activity, and HOA management.',
  INSURANCE: 'Master-policy status, coverage gaps, exclusions, and insurance-related obligations.',
};

function resolveFinancialRiskTier(
  findings?: HOAFindings,
  annualBudget?: HOAAnnualBudgetFindings,
): HOARiskTier {
  if (!findings && !annualBudget) return 'unreviewed';
  const reservePct = annualBudget?.reservePercentFunded ?? null;
  const hasActiveAssessment =
    findings?.specialAssessments?.some((a) => a.status === 'active') ?? false;
  const hasPendingAssessment =
    findings?.specialAssessments?.some((a) => a.status === 'pending') ?? false;
  const isDelinquent = findings?.delinquencyStatus === 'delinquent';
  const delinquentAmount = findings?.delinquentAmount ?? 0;
  const regularAssessment = findings?.assessmentAmount ?? 0;
  const delinquentBillingCycles = regularAssessment > 0 ? delinquentAmount / regularAssessment : 0;
  const activeAssessments =
    findings?.specialAssessments?.filter((a) => a.status === 'active') ?? [];
  const hasMaterialActiveAssessment = activeAssessments.some(
    (assessment) => regularAssessment > 0 && (assessment.amount ?? 0) >= regularAssessment * 6,
  );

  if (
    (isDelinquent && delinquentBillingCycles >= 6) ||
    (reservePct != null && reservePct < RESERVE_CRITICAL_PCT) ||
    hasMaterialActiveAssessment
  ) {
    return 'critical';
  }
  if (
    (isDelinquent && delinquentBillingCycles >= 3) ||
    hasActiveAssessment ||
    annualBudget?.specialAssessmentAnticipated ||
    (reservePct != null && reservePct < RESERVE_HEALTHY_TARGET_PCT)
  ) {
    return 'high';
  }
  if (isDelinquent || hasPendingAssessment || (annualBudget?.reserveDeficitPerUnit ?? 0) > 0) {
    return 'moderate';
  }
  return 'low';
}

function resolveLegalRiskTier(findings?: HOAFindings): HOARiskTier {
  if (!findings) return 'unreviewed';
  const openViolations = findings.violations?.filter((v) => v.status === 'open') ?? [];
  const pendingLitigation = (findings.litigation ?? []).filter((l) => l.status === 'pending');
  const unknownLitigation = (findings.litigation ?? []).filter((l) => l.status === 'unknown');

  if (pendingLitigation.length > 0) return 'critical';
  if (openViolations.length >= 2) return 'high';
  if (openViolations.length === 1 || unknownLitigation.length > 0) return 'moderate';
  return 'low';
}

function resolveEnvironmentalRiskTier(findings?: HOAEnvironmentalFindings): HOARiskTier {
  if (!findings) return 'unreviewed';
  const covenantRisks = findings.risks.filter(
    (r) => r.category === 'environmental-covenant' || r.category === 'environmental-monitoring',
  );
  if (covenantRisks.some((r) => r.severity === 'critical')) return 'critical';
  if (covenantRisks.some((r) => r.severity === 'high')) return 'high';
  if (
    covenantRisks.some((r) => r.severity === 'moderate') ||
    findings.reviewStatus === 'INDETERMINATE'
  ) {
    return 'moderate';
  }
  return 'low';
}

function resolveGovernanceRiskTier(
  environmental?: HOAEnvironmentalFindings,
  operatingRules?: HOAOperatingRulesFindings,
  boardMinutes?: HOABoardMinutesFindings,
): HOARiskTier {
  if (!environmental && !operatingRules && !boardMinutes) return 'unreviewed';

  const restrictionRisks = (environmental?.risks ?? []).filter(
    (r) =>
      r.category === 'use-restriction' ||
      r.category === 'leasing-restriction' ||
      r.category === 'architectural-control',
  );
  const latestMeeting = [...(boardMinutes?.meetings ?? [])]
    .filter((m) => m.date)
    .sort((a, b) => b.date.localeCompare(a.date))[0];

  if (
    restrictionRisks.some((r) => r.severity === 'critical') ||
    latestMeeting?.signedAndApproved === false
  ) {
    return 'critical';
  }
  if (
    restrictionRisks.some((r) => r.severity === 'high') ||
    (latestMeeting?.vacantSeats?.length ?? 0) > 0
  ) {
    return 'high';
  }
  const restrictionCount = operatingRules?.notableRestrictions?.length ?? 0;
  if (restrictionCount >= 1) {
    return 'moderate';
  }
  return 'low';
}

function resolveInsuranceRiskTier(
  findings?: HOAFindings,
  annualBudget?: HOAAnnualBudgetFindings,
  environmental?: HOAEnvironmentalFindings,
): HOARiskTier {
  const insurance = mergeInsuranceForDisplay(findings?.insurance, annualBudget?.insurance);
  const insuranceRisks = (environmental?.risks ?? []).filter(
    (r) => r.category === 'insurance-obligation',
  );

  if (!insurance && insuranceRisks.length === 0 && !environmental) return 'unreviewed';

  const policyExpired = insurance?.policyPeriodEnd
    ? new Date(insurance.policyPeriodEnd).getTime() < Date.now()
    : false;

  if (insurance?.hasMasterPolicy === false) return 'critical';
  if (policyExpired) return 'high';
  if (insuranceRisks.some((r) => r.severity === 'critical' || r.severity === 'high')) return 'high';
  if (insurance?.hasMasterPolicy == null && insuranceRisks.length === 0) return 'unreviewed';
  return 'low';
}

export function buildHOARiskRadarAxes(
  data: HOAChecklistResponse,
): Array<{ label: string; tier: HOARiskTier }> {
  return [
    {
      label: 'FINANCIAL',
      tier: resolveFinancialRiskTier(data.hoaFindings, data.hoaAnnualBudgetFindings),
    },
    {
      label: 'INSURANCE',
      tier: resolveInsuranceRiskTier(
        data.hoaFindings,
        data.hoaAnnualBudgetFindings,
        data.hoaEnvironmentalFindings,
      ),
    },
    { label: 'LEGAL', tier: resolveLegalRiskTier(data.hoaFindings) },
    {
      label: 'GOVERNANCE',
      tier: resolveGovernanceRiskTier(
        data.hoaEnvironmentalFindings,
        data.hoaOperatingRulesFindings,
        data.hoaBoardMinutesFindings,
      ),
    },
    {
      label: 'SITE',
      tier: resolveEnvironmentalRiskTier(data.hoaEnvironmentalFindings),
    },
  ];
}

// ─── Ask Hauser — same suggested-questions pattern as SubscoreDetailCard's
// RepairBurden tab (static list, click pushes into the chat drawer via
// openAskHauserWithQuestion), reworded for HOA due diligence. Left column
// holds the risk radar above.
function HOAAskHauserModule({
  data,
  stacked = false,
}: {
  data: HOAChecklistResponse;
  /** 27" instrument mode: the radar on top filling whatever height the sticky column gives it, Ask Hauser beneath. */
  stacked?: boolean;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { openAskHauserWithQuestion } = useDashboardLayout();
  const tooltipContainerRef = useRef<HTMLDivElement>(null);
  const [activeTopic, setActiveTopic] = useState<HOADueDiligenceTopic>('OVERVIEW');

  const radarAxes = useMemo(() => buildHOARiskRadarAxes(data), [data]);
  const radarSeries: RadarChartDataSeries[] = useMemo(() => {
    const radarData: RadarChartDataPoint[] = radarAxes.map((axis) => {
      const value = HOA_RISK_TIER_VALUE[axis.tier];
      return {
        label: axis.label,
        value,
        isNa: axis.tier === 'unreviewed',
        highlight: axis.tier !== 'unreviewed' && value < 60,
      };
    });
    return [
      {
        data: radarData,
        fillColor: 'var(--tx-fill)',
        strokeWidth: 3,
        segmentColors: radarAxes.map((axis) => HOA_RISK_TIER_COLOR[axis.tier]),
      },
    ];
  }, [radarAxes]);

  const questions = useMemo(
    () => buildHOADueDiligenceQuestions(data, activeTopic),
    [activeTopic, data],
  );

  return (
    <div
      ref={tooltipContainerRef}
      style={{
        background: COLORS.card,
        borderRadius: 25,
        // room to breathe on a computer: wider padding and gutter, and a floor tall enough for the longest topic's
        // questions so switching topics never resizes the card
        padding: isMobile ? 20 : 22,
        display: 'flex',
        flexDirection: isMobile || stacked ? 'column' : 'row',
        gap: stacked ? 28 : isMobile ? 20 : 28,
        position: 'relative',
        flex: stacked ? 1 : undefined,
        minHeight: stacked ? 0 : isMobile ? undefined : 'auto',
      }}>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 15,
            color: 'var(--tx-text)',
            fontSize: 17,
            fontWeight: 400,
            letterSpacing: '-0.015em',
          }}>
          <span>The HOA</span>
        </div>
        {stacked ? (
          // fitted like the Overview score cards: the radar takes whichever of the box's width or height runs out first
          <Box sx={{ flex: 1, minHeight: 280, position: 'relative', containerType: 'size' }}>
            <Box sx={{ width: 'min(100cqw, calc(100cqh + 55px))', mx: 'auto', height: '100%', display: 'flex', alignItems: 'center' }}>
              <RadarChart
                series={radarSeries}
                size={640}
                radiusFraction={0.37}
                contoured
                startAngleDegrees={-126}
                gridColor="rgba(255,255,255,0.1)"
                axisLabelColor="#BBB7AF"
                gridLevels={4}
                showLabels
                labelTooltips={HOA_RISK_RADAR_TOOLTIPS}
                tooltipContainerRef={tooltipContainerRef}
              />
            </Box>
          </Box>
        ) : (
        <div
          style={{
            // a computer: the radar sits in its own fixed-height stage at the top, so it never moves when the
            // questions beside it change length; mobile keeps its flowing layout
            flex: isMobile ? 1 : 'none',
            height: isMobile ? undefined : 360,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            paddingTop: isMobile ? 35 : 0,
            paddingBottom: isMobile ? 10 : 0,
          }}>
          {/* RadarChart has asymmetric internal vertical margins (-45px top /
                -10px bottom) that pull its visual center 17.5px above true
                center. Desktop uses -12.5px to position the complete HOA
                asset 30px above that baseline; mobile sits 10px lower. This offset is
                a fixed constant, not proportional to size (the
                size/2 term cancels out of the math), so it doesn't need to
                scale when size changes. */}
          <div style={{ transform: `translateY(${isMobile ? -2.5 : -12.5}px)` }}>
            <RadarChart
              series={radarSeries}
              size={isMobile ? 280 : 335}
              radiusFraction={0.46}
              contoured
              startAngleDegrees={-126}
              labelOffsetOverrides={{ 3: { y: -10 } }}
              gridColor="rgba(255,255,255,0.1)"
              axisLabelColor="#BBB7AF"
              gridLevels={4}
              showLabels={!isMobile}
              showValueTooltips={isMobile}
              labelTooltips={HOA_RISK_RADAR_TOOLTIPS}
              tooltipContainerRef={tooltipContainerRef}
            />
          </div>
        </div>
        )}
      </div>
      <div style={{ flex: stacked ? 'none' : 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <AskHauserList
          topics={{
            options: HOA_DUE_DILIGENCE_TOPICS,
            value: activeTopic,
            onChange: setActiveTopic,
            format: (topic) => topic.charAt(0) + topic.slice(1).toLowerCase(),
            ariaLabel: 'HOA due diligence topics',
          }}
          questions={questions.map(({ displayText, prompt }) => ({
            label: displayText,
            onAsk: () => openAskHauserWithQuestion(prompt, displayText),
          }))}
          report={{
            label: 'Full HOA report',
            onOpen: () =>
              openAskHauserWithQuestion(
              'Give me a structured HOA due-diligence summary using exactly these four plain-text ' +
                'sections. No markdown, no bold, no bullet points — just the labels and prose:\n\n' +
                "THE OVERVIEW\n[What the HOA's financial health and governance findings say together " +
                'about this property.]\n\n' +
                'BIGGEST RISK\n[The single finding a buyer cannot ignore — financial, legal, or ' +
                'covenant-related.]\n\n' +
                "NEGOTIATING LEVERAGE\n[What's disputable or disclosable before closing, and the " +
                'rough exposure.]\n\n' +
                'THE VERDICT\n[Direct framing: buy, walk, or negotiate — and why.]\n\n' +
                'Each section 2-3 sentences. Be direct, no filler.',
              'Get the full HOA report',
            ),
          }}
          spread={stacked}
          comfortable={!stacked && !isMobile}
          style={{ flex: 1 }}
        />
      </div>
    </div>
  );
}

// ─── Snapshot — big-number completion (HomeScoreCard pattern) + hero ──────

// where each hero flag is explained in full, so its row can take you there
const HERO_TARGET_CARD: Record<string, string> = {
  'environmental-covenant': 'Environmental & covenant findings',
  litigation: 'Risk & compliance',
  violation: 'Risk & compliance',
  'governance-cadence': 'Risk & compliance',
  delinquency: 'Financial summary',
  assessment: 'Financial summary',
  'reserve-underfunded': 'Financial summary',
};

const hoaCardAnchor = (title: string) => `hoa-card-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`;

function SnapshotModule({
  checklist,
  heroFindings,
  operatingRules,
  documents,
}: {
  checklist: ChecklistItem[];
  heroFindings: ReturnType<typeof pickHOAHeroFindings>;
  operatingRules?: HOAOperatingRulesFindings;
  documents?: HOAChecklistSource[];
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const requiredChecklist = checklist.filter(
    (item) => getHOADisclosureTier(item) === 'STATUTORY_REQUIRED',
  );
  const receivedCount = requiredChecklist.filter((c) => c.status !== 'NOT_RECEIVED').length;
  const hasAgeRestriction = operatingRules?.ageRestrictionPresent === true;

  const toneColor = (tone: string) =>
    tone === 'critical' ? COLORS.critical : tone === 'urgent' ? COLORS.urgent : 'var(--tx-muted)';
  const jumpTo = (variant: string) => {
    const title = HERO_TARGET_CARD[variant];
    if (title) document.getElementById(hoaCardAnchor(title))?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      style={{
        // the snapshot strip: the disclosure count, then the flags as rows
        background: COLORS.card,
        borderRadius: 25,
        padding: isMobile ? '18px 20px' : '20px 24px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        gap: isMobile ? 18 : 28,
      }}>
      <div style={{ flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
          <span
            style={{
              fontFamily: VALUE_FONT,
              fontSize: 48,
              fontWeight: 200,
              letterSpacing: '-0.07em',
              fontVariantNumeric: 'proportional-nums',
              lineHeight: 1,
              color: COLORS.text,
            }}>
            {receivedCount}
          </span>
          <span style={{ fontSize: 15, fontWeight: 300, color: 'var(--tx-muted)' }}>/{requiredChecklist.length}</span>
        </div>
        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 6 }}>Required disclosures</div>
      </div>
      {(hasAgeRestriction || heroFindings.length > 0) && (
        <>
          {!isMobile && <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--tx-line)', flexShrink: 0 }} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            {hasAgeRestriction && operatingRules && (
              <AgeRestrictionCallout findings={operatingRules} documents={documents} />
            )}
            {heroFindings.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', marginTop: hasAgeRestriction ? 12 : 0 }}>
                {heroFindings.map((finding, i) => (
                  <button
                    key={finding.variant}
                    type="button"
                    className="hx-snap-flag"
                    onClick={() => jumpTo(finding.variant)}
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 12,
                      width: '100%',
                      padding: '9px 0',
                      border: 0,
                      borderTop: i === 0 ? 'none' : '1px solid var(--tx-line)',
                      background: 'transparent',
                      textAlign: 'left',
                      fontFamily: "'Manrope', sans-serif",
                      cursor: HERO_TARGET_CARD[finding.variant] ? 'pointer' : 'default',
                    }}>
                    {/* the finding's own hairline glyph, in its urgency colour: the row's one cue, and a visual break in the copy */}
                    <span
                      aria-hidden="true"
                      style={{
                        display: 'inline-flex',
                        width: 16,
                        height: 16,
                        color: toneColor(finding.tone),
                        flexShrink: 0,
                        alignSelf: isMobile ? 'flex-start' : 'center',
                      }}>
                      {HOA_HERO_ICON_MAP[finding.variant]}
                    </span>
                    {/* a computer: headline and detail on one line; mobile: the detail wraps beneath the headline */}
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'flex-start' : 'baseline',
                        gap: isMobile ? 2 : 10,
                      }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tx-text)', flexShrink: 0 }}>{finding.headline}</span>
                      <span
                        style={{
                          minWidth: 0,
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: 1.4,
                          color: 'var(--tx-muted)',
                          whiteSpace: isMobile ? 'normal' : 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}>
                        {finding.detail}
                      </span>
                    </span>
                    {HERO_TARGET_CARD[finding.variant] && (
                      <ChevronDown size={14} style={{ transform: 'rotate(-90deg)', flexShrink: 0, alignSelf: 'center' }} />
                    )}
                  </button>
                ))}
                <style>
                  {'.hx-snap-flag > svg { color: var(--tx-faint); transition: color 0.12s ease, transform 0.12s ease; } ' +
                    '.hx-snap-flag:hover > svg { color: var(--tx-text); transform: rotate(-90deg) translateY(2px) !important; }'}
                </style>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function AgeRestrictionCallout({
  findings,
  documents,
}: {
  findings: HOAOperatingRulesFindings;
  documents?: HOAChecklistSource[];
}) {
  const requirement =
    typeof findings.minimumAge === 'number'
      ? `${findings.minimumAge}+ occupancy requirement`
      : 'Age-based occupancy restrictions apply';
  return (
    <div
      data-testid="hoa-age-restriction-callout"
      style={{
        padding: '12px 14px',
        borderRadius: 10,
        border: 0,
        background: `${COLORS.moderate}0D`,
      }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Building2 size={18} color={COLORS.moderate} aria-hidden="true" />
        <div>
          <div
            style={{
              color: COLORS.moderate,
              fontSize: 10,
              fontWeight: 600,
            }}>
            Age-Restricted Community
          </div>
          <div style={{ color: COLORS.text, fontSize: 14, fontWeight: 600, marginTop: 2 }}>
            {requirement}
          </div>
        </div>
      </div>
      {findings.ageRestrictionApplicability && (
        <div style={{ color: COLORS.textDim, fontSize: 11, lineHeight: 1.45, marginTop: 7 }}>
          {findings.ageRestrictionApplicability}
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <DocumentCitationRow
          citations={ageRestrictionCitations(findings, documents)}
          pushToBottom={false}
        />
      </div>
    </div>
  );
}

// ─── Financial Summary — stat cards (NHD Tax Profile pattern) ─────────────

function FinancialSummaryModule({
  findings,
  annualBudget,
  transfer,
  documents,
}: {
  findings?: HOAFindings;
  annualBudget?: HOAAnnualBudgetFindings;
  transfer?: HOATransferFindings;
  documents?: HOAChecklistSource[];
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (!findings && !annualBudget && !transfer) {
    return (
      <ModuleCard title="Financial summary">
        <EmptyState text="No demand statement processed yet — assessment and fee details will appear here once one is uploaded." />
      </ModuleCard>
    );
  }

  const isDelinquent = findings?.delinquencyStatus === 'delinquent';
  const reserveFundingStat = annualBudget
    ? resolveReserveFundingStat(annualBudget, new Date())
    : undefined;

  return (
    <ModuleCard
      title="Financial summary"
      headerRight={
        <CardCaution
          checks={extractionStalenessChecks([
            { label: 'Demand Statement', extractedAt: findings?.extractedAt },
            {
              label: 'Annual Budget / Reserve Study',
              extractedAt: annualBudget?.extractedAt,
              monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
            },
          ])}
        />
      }>
      {(findings || reserveFundingStat || transfer) && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(3, 1fr)',
            gap: isMobile ? '20px 16px' : 10,
          }}>
          {findings && (
            <>
              <Stat
                stacked={isMobile}
                label="Assessment"
                value={fmtMoney(findings.assessmentAmount)}
                detail={fmtAssessmentFrequency(findings.assessmentFrequency)}
                valueFontFamily={VALUE_FONT}
                valueFontSize={22}
              />
              <Stat
                stacked={isMobile}
                label="Delinquency"
                value={isDelinquent ? fmtMoney(findings.delinquentAmount) : 'Current'}
                tone={isDelinquent ? COLORS.critical : undefined}
                valueFontFamily={VALUE_FONT}
                valueFontSize={22}
              />
            </>
          )}
          {reserveFundingStat && (
            <div>
              <Stat
                stacked={isMobile}
                label={isMobile ? 'Reserve funding' : 'Reserve Funding'}
                value={`${reserveFundingStat.percentFunded.toFixed(1)}%`}
                detail={reserveFundingStat.detail}
                detailPrefix=""
                tone={reserveBarTone(reserveFundingStat.percentFunded)}
                valueFontFamily={VALUE_FONT}
                valueFontSize={22}
              />
            </div>
          )}
          {transfer?.statutoryPackageEstimatedFee != null && (
            <Stat
              stacked={isMobile}
              label={isMobile ? 'Package fee' : 'Package Fee'}
              value={fmtMoney(transfer.statutoryPackageEstimatedFee)}
              detail="Statutory estimate"
              detailPrefix=""
              valueFontFamily={VALUE_FONT}
              valueFontSize={22}
            />
          )}
          {transfer?.demandDocumentFee != null && (
            <Stat
              stacked={isMobile}
              label={isMobile ? 'Demand fee' : 'Demand Fee'}
              value={fmtMoney(transfer.demandDocumentFee)}
              valueFontFamily={VALUE_FONT}
              valueFontSize={22}
            />
          )}
          {transfer?.specialAssessmentDocumentFee != null && (
            <Stat
              stacked={isMobile}
              label={isMobile ? 'Assessment doc fee' : 'Assessment Doc Fee'}
              value={fmtMoney(transfer.specialAssessmentDocumentFee)}
              valueFontFamily={VALUE_FONT}
              valueFontSize={22}
            />
          )}
        </div>
      )}
      {(findings || annualBudget) && (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {annualBudget && <ReserveFundingTrend annualBudget={annualBudget} />}
          {findings && (
            <>
              <div
                style={{ height: 1, background: COLORS.border, marginTop: 20, marginBottom: 20 }}
              />
              <ListSubsection
                label="Special assessments"
                items={findings.specialAssessments}
                empty="None disclosed."
                renderItem={(s: HOASpecialAssessment, i) => (
                  <ListRow
                    key={i}
                    primary={s.description}
                    secondary={s.status ?? undefined}
                    trailing={fmtMoney(s.amount)}
                    flagged={s.status === 'active' || s.status === 'approved'}
                  />
                )}
              />
            </>
          )}
        </div>
      )}
      <DocumentCitationRow
        citations={mergeCitations(
          [
            ...citationsForLogicalDocuments(
              findings ? documents : undefined,
              ['HOA_DEMAND', 'HOA_ASSESSMENT_CHANGE_NOTICE', 'HOA_VIOLATION_NOTICE'],
              citationsForRecordIds(
                findings?.documentRecordIds ??
                  (findings?.documentRecordId ? [findings.documentRecordId] : []),
                'Demand / Assessment Disclosure',
              ),
            ),
            ...citationsForLogicalDocuments(
              annualBudget ? documents : undefined,
              ['HOA_ANNUAL_BUDGET', 'HOA_FINANCIAL_STATEMENT'],
              citationsForRecordIds(
                annualBudget?.financialDocumentRecordIds ??
                  (annualBudget?.documentRecordId ? [annualBudget.documentRecordId] : []),
                'Annual Budget / Reserve Study',
              ),
            ),
            ...citationsForLogicalDocuments(
              transfer ? documents : undefined,
              ['HOA_TRANSFER_DISCLOSURE_FORM', 'HOA_CLOSING_INFORMATION'],
              citationsForRecordIds(
                transfer?.documentRecordIds ??
                  (transfer?.documentRecordId ? [transfer.documentRecordId] : []),
                'Transfer / Closing Information',
              ),
            ),
          ].filter((c): c is DocumentCitation => c != null),
        )}
      />
    </ModuleCard>
  );
}

function reserveBarTone(pct: number): string | undefined {
  // colour only where it means something: below the healthy target
  if (pct < RESERVE_CRITICAL_PCT) return COLORS.critical;
  if (pct < RESERVE_HEALTHY_TARGET_PCT) return COLORS.urgent;
  return undefined;
}

function reserveFiscalYearIdentity(fiscalYear: string): string {
  const normalized = fiscalYear.trim().replace(/\s+/g, '');
  const range = normalized.match(/^(\d{4})[-/](\d{2}|\d{4})$/);
  if (!range) return normalized;

  const startYear = range[1];
  const rawEndYear = range[2];
  const endYear = rawEndYear.length === 2 ? `${startYear.slice(0, 2)}${rawEndYear}` : rawEndYear;
  return `${startYear}/${endYear}`;
}

type ReserveFundingStat = {
  percentFunded: number;
  detail: string;
};

function fiscalYearProjectionContainsDate(fiscalYear: string, referenceDate: Date): boolean {
  const normalized = reserveFiscalYearIdentity(fiscalYear);
  const singleYear = normalized.match(/^(\d{4})$/);
  if (singleYear) return Number(singleYear[1]) === referenceDate.getFullYear();

  const range = normalized.match(/^(\d{4})\/(\d{4})$/);
  if (!range) return false;
  const startYear = Number(range[1]);
  const endYear = Number(range[2]);
  const year = referenceDate.getFullYear();
  if (year > startYear && year < endYear) return true;
  if (endYear !== startYear + 1) return year === startYear || year === endYear;

  // A two-year fiscal label ordinarily crosses the calendar-year boundary.
  // Without an explicit fiscal-year-end month, use the conventional midpoint
  // only for this fallback; an explicitly reported current value always wins.
  return year === startYear
    ? referenceDate.getMonth() >= 6
    : year === endYear && referenceDate.getMonth() < 6;
}

export function resolveReserveFundingStat(
  annualBudget: HOAAnnualBudgetFindings,
  referenceDate: Date,
): ReserveFundingStat | undefined {
  if (
    typeof annualBudget.reservePercentFunded === 'number' &&
    Number.isFinite(annualBudget.reservePercentFunded)
  ) {
    return {
      percentFunded: annualBudget.reservePercentFunded,
      detail: 'Current snapshot',
    };
  }

  const currentProjection = annualBudget.reserveProjection?.find(({ fiscalYear }) =>
    fiscalYearProjectionContainsDate(fiscalYear, referenceDate),
  );
  return currentProjection
    ? {
        percentFunded: currentProjection.percentFunded,
        detail: `Projected · FY ${currentProjection.fiscalYear}`,
      }
    : undefined;
}

// Reserve-percent-funded trajectory — a multi-year projection, not a single
// snapshot, because the real Reserve Study showed percent funded dipping
// mid-projection before recovering; a point-in-time number would have
// hidden that entirely. Scaled 0–100 (not to the data's own range) so a low
// bar looks genuinely low, not merely short relative to its neighbors.
function ReserveFundingTrend({ annualBudget }: { annualBudget: HOAAnnualBudgetFindings }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const projection = annualBudget.reserveProjection ?? [];
  const currentSnapshot =
    typeof annualBudget.reservePercentFunded === 'number' &&
    Number.isFinite(annualBudget.reservePercentFunded)
      ? Math.max(0, Math.min(100, annualBudget.reservePercentFunded))
      : undefined;
  const seenFiscalYears = new Set<string>();
  const chronologicalProjection = [...projection]
    .sort((a, b) => Number.parseInt(a.fiscalYear, 10) - Number.parseInt(b.fiscalYear, 10))
    .filter((year) => {
      const identity = reserveFiscalYearIdentity(year.fiscalYear);
      if (seenFiscalYears.has(identity)) return false;
      seenFiscalYears.add(identity);
      return true;
    });
  // Preserve the report's own timeline. Historical diligence packages are
  // still evidence of what the report projected at the time, so filtering
  // against today's calendar year would silently discard valid source data.
  // Never synthesize missing years; show only the first five explicit years.
  const visibleProjection = chronologicalProjection.slice(0, 5);

  if (projection.length === 0 && !annualBudget.specialAssessmentAnticipated) return null;

  // The current-percent hero number now lives in the stat pill row above
  // (Financial Summary's third card) — this section is purely the
  // multi-year trajectory, so it gets the full chart height that used to
  // be split with that redundant number. On desktop the chart box is
  // flex-grown to fill whatever vertical space Financial Summary has to
  // spare (the card's height matches its row sibling, Risk & Compliance),
  // so bar heights and the target-line position are expressed as CSS
  // percentages of that box's actual (layout-time) height. On mobile there
  // is no row sibling to stretch against — the card's height is just its
  // own content, so the box's height only comes from `minHeight` rather
  // than an actual resolved size, which some mobile browsers (Safari)
  // don't treat as definite enough to resolve the bars' percentage heights
  // against, rendering flat/invisible bars. Mobile gets a real `height`
  // instead of `flex + minHeight` so there's no percentage-through-flex
  // chain to fail.
  const chartMinHeight = 220;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <div
        style={{
          position: 'relative',
          top: 15,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}>
        <SubLabel>Projected reserve funding</SubLabel>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 7,
            flexShrink: 0,
          }}>
          <span
            aria-hidden="true"
            style={{
              width: 20,
              borderTop: '1px dashed var(--tx-faint)',
            }}
          />
          <span
            style={{
              fontSize: 11,
              fontWeight: 500,
              color: 'var(--tx-muted)',
              whiteSpace: 'nowrap',
            }}>
            {RESERVE_HEALTHY_TARGET_PCT}% healthy target
          </span>
        </div>
      </div>
      {visibleProjection.length > 0 && (
        <div
          style={{
            position: 'relative',
            flex: isMobile ? undefined : 1,
            height: isMobile ? chartMinHeight : undefined,
            minHeight: isMobile ? undefined : chartMinHeight,
            marginTop: 36,
          }}>
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: `${RESERVE_HEALTHY_TARGET_PCT}%`,
              borderTop: '1px dashed var(--tx-faint)',
            }}
          />
          {/* Bar bottoms must land on the same zero-line the target line is
              measured from (the box's true bottom edge). The fiscal-year
              label used to live inside this flex-end-justified column,
              which pushed the whole [%, bar, year] group up by the label's
              own height — shifting every bar's effective baseline off zero
              and making the 70% line read as much closer to the bars than
              their real percentages. The year label now renders in its own
              row below, outside this height-constrained box. */}
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', gap: 10 }}>
            {visibleProjection.map((p, index) => {
              const tone = reserveBarTone(p.percentFunded);
              return (
                <div
                  key={p.fiscalYear}
                  style={{
                    position: 'relative',
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                  }}>
                  {/* a narrow pill (the HomeDNA / by-system bar family) with its light figure riding above */}
                  <div
                    style={{
                      position: 'relative',
                      width: 6,
                      height: `max(6px, ${p.percentFunded}%)`,
                      background: tone ?? 'var(--tx-muted)',
                      borderRadius: 3,
                    }}>
                    <span
                      style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: 'calc(100% + 8px)',
                        transform: 'translateX(-50%)',
                        fontSize: 15,
                        fontWeight: 300,
                        letterSpacing: '-0.03em',
                        color: tone ?? 'var(--tx-text)',
                        lineHeight: 1,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}>
                      {p.percentFunded.toFixed(0)}%
                    </span>
                  </div>
                  {index === 0 && currentSnapshot != null && (
                    <Tip
                      text={`Current snapshot: ${currentSnapshot.toFixed(1)}%`}
                      style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: `${currentSnapshot}%`,
                        width: 18,
                        height: 8,
                        transform: 'translate(-50%, 1px)',
                        zIndex: 1,
                        borderBottom: 'none',
                      }}>
                      <span
                        role="img"
                        aria-label={`Current snapshot: ${currentSnapshot.toFixed(1)}%`}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: 0,
                          right: 0,
                          borderTop: '1px solid var(--tx-text)',
                          transform: 'translateY(-50%)',
                        }}
                      />
                    </Tip>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {visibleProjection.length > 0 && (
        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
          {visibleProjection.map((p) => (
            <span
              key={p.fiscalYear}
              style={{
                flex: 1,
                textAlign: 'center',
                fontSize: 11,
                color: 'var(--tx-muted)',
                fontWeight: 500,
              }}>
              {p.fiscalYear}
            </span>
          ))}
        </div>
      )}
      {visibleProjection.length > 0 && (
        <div style={{ color: 'var(--tx-faint)', fontSize: 11, fontWeight: 500, lineHeight: 1.45, marginTop: 12 }}>
          Projections reflect the association&apos;s current funding plan and are not guaranteed;
          actual funding may vary with board decisions, expenses, and project timing.
        </div>
      )}
      {annualBudget.specialAssessmentAnticipated && (
        <div style={{ marginTop: 10 }}>
          <ListRow
            primary="Special assessment anticipated per reserve study"
            tone="urgent"
            trailing="Watch"
          />
        </div>
      )}
    </div>
  );
}

// ─── Risk & Compliance — flagged/prioritized rows (Repair Planner pattern) ─

// Defensive filter for a pre-existing extraction bug: older demand-statement
// extractions sometimes populated coverageNotes with agent/broker contact
// details (name, phone, fax, email) instead of actual coverage exclusions —
// the schema description that caused this has since been fixed, but that
// fix only applies to *new* extractions, not data already saved for a
// property. Strips any sentence that looks like a contact line so this card
// isn't stuck showing garbage until the document is reprocessed.
const CONTACT_INFO_PATTERN =
  /\b(agent|broker)\b[^.]*(:|\bis\b)|\d[\d\s().-]{7,}\d|[\w.+-]+@[\w-]+\.[\w.-]+/i;

function sanitizeCoverageNotes(notes: string | undefined): string | null {
  if (!notes) return null;
  const cleaned = notes
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => !CONTACT_INFO_PATTERN.test(sentence))
    .join(' ')
    .trim();
  return cleaned.length > 0 ? cleaned : null;
}

const INSURANCE_COVERAGE_LABELS: Record<string, string> = {
  'property coverage limit': 'Property coverage limit',
  'liability per occurrence': 'Liability / occurrence',
  'liability aggregate': 'Liability aggregate',
  'property deductible': 'Property deductible',
  'water-damage deductible': 'Water-damage deductible',
};

export interface InsuranceCoverageDetail {
  label: string;
  value: string;
}

function parseInsuranceCoverageFact(value: string): InsuranceCoverageDetail | null {
  const match = value.match(
    /^(property coverage limit|liability per occurrence|liability aggregate|property deductible|water-damage deductible):\s*\$([\d,]+(?:\.\d{1,2})?)$/i,
  );
  if (!match) return null;

  const amount = Number(match[2].replace(/,/g, ''));
  if (!Number.isFinite(amount)) return null;

  return {
    label: INSURANCE_COVERAGE_LABELS[match[1].toLowerCase()],
    value: new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount),
  };
}

export function buildInsuranceCoverageDetails(
  notes: string | undefined,
): InsuranceCoverageDetail[] {
  const cleaned = sanitizeCoverageNotes(notes);
  if (!cleaned) return [];

  const details = new Map<string, InsuranceCoverageDetail>();
  for (const rawItem of cleaned.split(/\s*;\s*/)) {
    const item = rawItem.replace(/^[.;:\s]+|[.;:\s]+$/g, '').trim();
    const detail = parseInsuranceCoverageFact(item);
    if (detail && !details.has(detail.label)) details.set(detail.label, detail);
  }
  return [...details.values()];
}

// Older extractions stored every coverage fact in one semicolon-heavy blob.
// Keep the underlying source intact for Ask Hauser, but turn that blob into a
// short, deduplicated buyer-facing list for this card. Dedicated-field echoes
// such as "Property coverage limit: $57,813,898" are rendered separately as
// compact coverage facts rather than competing with the narrative-item cap.
export function buildInsuranceDetailItems(notes: string | undefined): string[] {
  const cleaned = sanitizeCoverageNotes(notes);
  if (!cleaned) return [];

  const items: string[] = [];
  const seenText = new Set<string>();
  for (const rawItem of cleaned.split(/\s*;\s*|(?<=[.!?])\s+(?=[A-Z$])/)) {
    const item = rawItem.replace(/^[.;:\s]+|[.;:\s]+$/g, '').trim();
    if (!item) continue;

    if (parseInsuranceCoverageFact(item)) continue;

    const identity = item
      .toLowerCase()
      .replace(/[^a-z0-9$]+/g, ' ')
      .trim();
    if (seenText.has(identity)) continue;

    seenText.add(identity);
    items.push(item);
    if (items.length === 6) break;
  }
  return items;
}

function RiskComplianceModule({
  findings,
  annualBudget,
  boardMinutes,
  boardMinutesReceived,
  documents,
}: {
  findings?: HOAFindings;
  annualBudget?: HOAAnnualBudgetFindings;
  boardMinutes?: HOABoardMinutesFindings;
  boardMinutesReceived: boolean;
  documents?: HOAChecklistSource[];
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [insuranceDetailsOpen, setInsuranceDetailsOpen] = useState(false);

  if (!findings && !boardMinutesReceived && !boardMinutes) {
    return (
      <ModuleCard title="Risk & compliance">
        <EmptyState text="No demand statement or board minutes processed yet — risk, compliance, and governance findings will appear here once uploaded." />
      </ModuleCard>
    );
  }

  // Same precedence Financial Summary's reserve/insurance data uses: the
  // dedicated Annual Budget/Reserve Study/Insurance Summary document is the
  // one that actually states earthquake/flood coverage, so it wins over the
  // Demand Statement's lighter insurance blurb when both exist — one
  // insurance picture, not two that can quietly disagree.
  const insurance = mergeInsuranceForDisplay(findings?.insurance, annualBudget?.insurance);
  const noEarthquake = insurance?.hasEarthquakeInsurance === false;
  const noFlood = insurance?.hasFloodInsurance === false;
  const insuranceDetailItems = buildInsuranceDetailItems(insurance?.coverageNotes);
  const insuranceCoverageDetails = buildInsuranceCoverageDetails(insurance?.coverageNotes);
  const hasInsuranceDetails =
    insuranceDetailItems.length > 0 || insuranceCoverageDetails.length > 0;

  return (
    <ModuleCard
      title="Risk & compliance"
      headerRight={
        <CardCaution
          checks={[
            policyPeriodExpiredCheck(insurance?.policyPeriodEnd),
            ...extractionStalenessChecks([
              { label: 'Demand Statement', extractedAt: findings?.extractedAt },
              {
                label: 'Annual Budget / Reserve Study',
                extractedAt: annualBudget?.extractedAt,
                monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
              },
              {
                label: 'Board Minutes',
                extractedAt: boardMinutes?.extractedAt,
                monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
              },
            ]),
          ]}
        />
      }>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
          gap: 12,
        }}>
        <RiskCompliancePanel>
          <div
            role={hasInsuranceDetails ? 'button' : undefined}
            tabIndex={hasInsuranceDetails ? 0 : undefined}
            aria-label={hasInsuranceDetails ? 'Insurance details' : undefined}
            aria-expanded={hasInsuranceDetails ? insuranceDetailsOpen : undefined}
            onClick={
              hasInsuranceDetails ? () => setInsuranceDetailsOpen((open) => !open) : undefined
            }
            onKeyDown={
              hasInsuranceDetails
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setInsuranceDetailsOpen((open) => !open);
                    }
                  }
                : undefined
            }
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              cursor: hasInsuranceDetails ? 'pointer' : 'default',
              outline: 'none',
            }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 12,
              }}>
              <SubLabel>Insurance</SubLabel>
              {hasInsuranceDetails && (
                <span
                  aria-hidden="true"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    color: COLORS.textDim,
                    fontSize: 12,
                    lineHeight: 1.4,
                    flexShrink: 0,
                  }}>
                  Details
                  <ChevronDown
                    size={15}
                    style={{
                      transform: insuranceDetailsOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 160ms ease',
                    }}
                  />
                </span>
              )}
            </div>
            {insurance ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  marginTop: 2,
                  fontSize: 13,
                  fontWeight: 500,
                  color: COLORS.text,
                  lineHeight: 1.5,
                }}>
                {hasInsuranceDetails && insuranceDetailsOpen && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 5,
                      marginBottom: 6,
                    }}>
                    {insuranceCoverageDetails.length > 0 && (
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'minmax(0, 1fr) auto',
                          columnGap: 12,
                          rowGap: 4,
                          marginBottom: insuranceDetailItems.length > 0 ? 5 : 0,
                          paddingBottom: insuranceDetailItems.length > 0 ? 8 : 0,
                          borderBottom:
                            insuranceDetailItems.length > 0
                              ? `1px solid ${COLORS.border}`
                              : undefined,
                        }}>
                        {insuranceCoverageDetails.map((detail) => (
                          <Fragment key={detail.label}>
                            <span
                              style={{
                                color: COLORS.textDim,
                                fontSize: 10,
                                lineHeight: 1.35,
                              }}>
                              {detail.label}
                            </span>
                            <span
                              style={{
                                color: COLORS.text,
                                fontSize: 10,
                                fontWeight: 600,
                                lineHeight: 1.35,
                                textAlign: 'right',
                              }}>
                              {detail.value}
                            </span>
                          </Fragment>
                        ))}
                      </div>
                    )}
                    {insuranceDetailItems.map((item) => (
                      <div
                        key={item}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '4px minmax(0, 1fr)',
                          alignItems: 'start',
                          gap: 7,
                          fontSize: 11,
                          lineHeight: 1.45,
                          color: COLORS.textDim,
                        }}>
                        <span
                          aria-hidden="true"
                          style={{
                            width: 3,
                            height: 3,
                            marginTop: 6,
                            borderRadius: '50%',
                            background: COLORS.textDim,
                          }}
                        />
                        <span>{item}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div>
                  {insurance.hasMasterPolicy === true
                    ? 'Master policy confirmed'
                    : insurance.hasMasterPolicy === false
                    ? 'No master policy on file'
                    : 'Master policy status unknown'}
                  {insurance.carrier && ` — ${insurance.carrier}`}
                </div>
                {(noEarthquake || noFlood) && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr',
                      gap: 4,
                      paddingTop: 10,
                    }}>
                    {noEarthquake && <InsuranceGapChip label="No earthquake insurance" />}
                    {noFlood && <InsuranceGapChip label="No flood insurance" />}
                  </div>
                )}
              </div>
            ) : (
              <EmptyState text="Not disclosed." />
            )}
          </div>
        </RiskCompliancePanel>
        <RiskCompliancePanel>
          <ListSubsection
            label="Violations"
            splitContent={!isMobile}
            items={findings?.violations ?? []}
            empty={findings ? 'None disclosed.' : 'Demand statement not processed.'}
            renderItem={(v: HOAViolation, i) => (
              <ComplianceFindingRow
                key={i}
                description={v.description}
                detail={fmtDate(v.noticeDate)}
                status={v.status ?? undefined}
                flagged={v.status === 'open'}
              />
            )}
          />
        </RiskCompliancePanel>
        <RiskCompliancePanel>
          <ListSubsection
            label="Litigation"
            splitContent={!isMobile}
            items={findings?.litigation ?? []}
            empty={findings ? 'None disclosed.' : 'Demand statement not processed.'}
            renderItem={(l: HOALitigationMatter, i) => (
              <ComplianceFindingRow
                key={i}
                description={l.description}
                status={l.status}
                flagged={l.status === 'pending'}
              />
            )}
          />
        </RiskCompliancePanel>
      </div>
      <BoardGovernancePanel findings={boardMinutes} documentReceived={boardMinutesReceived} />
      <DocumentCitationRow
        citations={mergeCitations(
          [
            ...citationsForLogicalDocuments(
              findings ? documents : undefined,
              ['HOA_DEMAND', 'HOA_ASSESSMENT_CHANGE_NOTICE', 'HOA_VIOLATION_NOTICE'],
              citationsForRecordIds(
                findings?.documentRecordIds ??
                  (findings?.documentRecordId ? [findings.documentRecordId] : []),
                'Demand / Assessment Disclosure',
              ),
            ),
            ...citationsForLogicalDocuments(
              annualBudget?.insurance ? documents : undefined,
              ['HOA_INSURANCE_SUMMARY', 'HOA_ANNUAL_BUDGET'],
              citationsForRecordIds(
                annualBudget?.insuranceDocumentRecordIds ??
                  (annualBudget?.insurance && annualBudget.documentRecordId
                    ? [annualBudget.documentRecordId]
                    : []),
                'Insurance Summary',
              ),
            ),
            ...citationsForLogicalDocuments(
              boardMinutes ? documents : undefined,
              ['HOA_BOARD_MINUTES'],
              citationsForRecordIds(
                boardMinutes?.meetings.some((meeting) => meeting.documentRecordId)
                  ? boardMinutes.meetings.map((meeting) => meeting.documentRecordId)
                  : [boardMinutes?.documentRecordId],
                'Board Minutes',
              ),
            ),
          ].filter((c): c is DocumentCitation => c != null),
        )}
      />
    </ModuleCard>
  );
}

function InsuranceGapChip({ label }: { label: string }) {
  return (
    <div
      style={{
        // a coverage gap reads as one quiet flagged line: hairline glyph + text in the caution tone, no box
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}>
      <AlertTriangle size={14} style={{ color: COLORS.urgent, flexShrink: 0 }} />
      <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.urgent, whiteSpace: 'nowrap' }}>{label}</span>
    </div>
  );
}

function ComplianceFindingRow({
  description,
  detail,
  status,
}: {
  description: string;
  detail?: string;
  status?: string;
  flagged?: boolean;
}) {
  return (
    <div
      style={{
        padding: '4px 0',
      }}>
      {status && (
        <span
          style={{
            display: 'inline-flex',
            padding: '3px 6px',
            borderRadius: '6px',
            background: 'rgba(var(--tx-ink-rgb), 0.06)',
            fontSize: 11,
            fontWeight: 500,
            lineHeight: 1.1,
            color: 'var(--tx-muted)',
          }}>
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      )}
      <div style={{ fontSize: 13, fontWeight: 500, color: COLORS.text, lineHeight: 1.4, marginTop: status ? 8 : 0 }}>
        {description}
      </div>
      {detail && <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 3 }}>{detail}</div>}
    </div>
  );
}

// Groups each subsection into its own panel — same nested-box fill as
// Stat/ListRow (`#2a2a2a`), no stroke — so Violations, Litigation, and
// Insurance read as three distinct blocks instead of one unbroken stack of
// labels, without an extra outline competing with the card's own border.
function RiskCompliancePanel({
  children,
  minHeight,
}: {
  children: React.ReactNode;
  /** Stretches the panel to a fixed floor height — used for Violations/
   *  Litigation so the two read as substantial card real estate even when
   *  "None disclosed" is the entire content. */
  minHeight?: number;
}) {
  return (
    <div
      style={{
        // the tx-vz-card tile: a tonal well inside the sheet, no shadow, no sheen
        background: 'rgba(var(--tx-ink-rgb), 0.05)',
        borderRadius: '16px',
        padding: 16,
        minHeight,
        height: '100%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}>
      {children}
    </div>
  );
}

// ─── Board Activity — horizontal cadence summary + vertical timeline ──────
// (BoardMinutesFindings)
//
// Consecutive "no meeting held" months are collapsed into one compact row
// instead of one row per month — with five real HOA gaps that's often most
// of the timeline, and repeating an identical line five times buries the
// months that actually have content (approvals, liens, balances) under
// visual noise. Only the trailing gap — the one still open as of the most
// recent record — gets flagged, matching the same threshold the summary
// card's hero finding uses, so the two never disagree about what counts as
// a real lapse vs. a single skipped month.

type BoardTimelineEntry =
  | { type: 'meeting'; meeting: HOABoardMeetingEntry }
  | { type: 'gap'; dates: string[] };

function buildBoardTimeline(sorted: HOABoardMeetingEntry[]): BoardTimelineEntry[] {
  const entries: BoardTimelineEntry[] = [];
  let gapDates: string[] = [];
  const flushGap = () => {
    if (gapDates.length > 0) {
      entries.push({ type: 'gap', dates: gapDates });
      gapDates = [];
    }
  };
  for (const m of sorted) {
    if (m.heldMeeting) {
      flushGap();
      entries.push({ type: 'meeting', meeting: m });
    } else {
      gapDates.push(m.date);
    }
  }
  flushGap();
  return entries;
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

interface MonthlyCadenceDot {
  key: string;
  label: string;
  held: boolean;
}

// The cadence summary's x-axis is a real Jan–Dec calendar for the selected
// year, not one dot per raw meeting record — some months have more than one
// HOABoardMeetingEntry (e.g. a regular meeting plus a separate no-meeting
// placeholder date), and a plain 1:1 mapping produced repeated month labels
// with no gaps for genuinely missing months. A month is filled if any
// record for it that year was held.
export function buildCalendarMonths(
  yearMeetings: HOABoardMeetingEntry[],
  year: number,
): MonthlyCadenceDot[] {
  const heldMonths = new Set<number>();
  for (const m of yearMeetings) {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(m.date);
    if (!match || Number(match[1]) !== year || !m.heldMeeting) continue;
    const month = Number(match[2]) - 1;
    if (month < 0 || month > 11) continue;
    heldMonths.add(month);
  }
  return MONTH_LABELS.map((label, i) => ({ key: `${year}-${i}`, label, held: heldMonths.has(i) }));
}

export function mergeInsuranceForDisplay(
  demandInsurance?: HOAInsuranceSummary,
  annualInsurance?: HOAInsuranceSummary,
): HOAInsuranceSummary | undefined {
  if (!demandInsurance && !annualInsurance) return undefined;
  const definedAnnual = Object.fromEntries(
    Object.entries(annualInsurance ?? {}).filter(([, value]) => value !== undefined),
  ) as Partial<HOAInsuranceSummary>;
  return { ...demandInsurance, ...definedAnnual } as HOAInsuranceSummary;
}

// A recorded cadence gap (GOVERNANCE_CADENCE_GAP_THRESHOLD, used by the
// vertical timeline's "Ongoing" badge) means the board itself skipped
// meetings — it's a different signal from the *document* simply not
// covering recent months at all, e.g. board minutes on file only through
// Dec 2024 with nothing from 2025 or 2026. That's not a governance lapse,
// it's a stale/incomplete extraction — worth a distinct, softer caution
// rather than reusing the 2-month cadence-gap threshold, which would fire
// on perfectly normal upload lag. 12 months, not a guess: Civil Code §4525
// entitles a buyer to minutes from "the previous 12 months" — this matches
// EXTENDED_STALE_MONTHS_THRESHOLD above and the same statute, just measured
// against the board's actual last-meeting date instead of extractedAt.
const STALE_RECORD_MONTHS_THRESHOLD = 12;

function monthsSince(iso: string, now: Date): number {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  return (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
}

function BoardGovernancePanel({
  findings,
  documentReceived,
}: {
  findings?: HOABoardMinutesFindings;
  documentReceived: boolean;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const allSorted = useMemo(
    () => [...(findings?.meetings ?? [])].sort((a, b) => a.date.localeCompare(b.date)),
    [findings],
  );
  const years = useMemo(() => {
    const set = new Set(
      allSorted.map((m) => new Date(m.date).getFullYear()).filter((y) => !Number.isNaN(y)),
    );
    return [...set].sort((a, b) => a - b);
  }, [allSorted]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const activeYear = selectedYear ?? years[years.length - 1] ?? null;

  if (!findings || findings.meetings.length === 0) {
    return (
      <RiskCompliancePanel>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <SubLabel>Board Activity</SubLabel>
          <div style={{ marginTop: 'auto', paddingTop: 24 }}>
            <ComplianceFindingRow
              status={documentReceived ? 'Review pending' : 'Not provided'}
              description={
                documentReceived
                  ? 'Board minutes are on file, but the meeting review has not completed.'
                  : 'Board minutes were not provided for governance review.'
              }
            />
          </div>
        </div>
      </RiskCompliancePanel>
    );
  }

  const yearMeetings =
    activeYear != null
      ? allSorted.filter((m) => new Date(m.date).getFullYear() === activeYear)
      : allSorted;
  const timeline = buildBoardTimeline(yearMeetings);
  const calendarMonths = activeYear != null ? buildCalendarMonths(yearMeetings, activeYear) : [];

  const mostRecent = allSorted[allSorted.length - 1];
  const staleMonths = mostRecent ? monthsSince(mostRecent.date, new Date()) : 0;
  const isStale = staleMonths >= STALE_RECORD_MONTHS_THRESHOLD;
  const consecutiveGapMonths = computeConsecutiveMonthsWithoutMeeting(allSorted);
  const hasCadenceConcern = consecutiveGapMonths >= GOVERNANCE_CADENCE_GAP_THRESHOLD;
  const heldMeetingCount = yearMeetings.filter((meeting) => meeting.heldMeeting).length;
  const status = hasCadenceConcern ? 'Attention' : isStale ? 'Outdated' : 'Reviewed';
  const summary = hasCadenceConcern
    ? `${consecutiveGapMonths} consecutive months without a meeting require advisor review.`
    : isStale && mostRecent
    ? `Latest board activity on file is from ${fmtDate(mostRecent.date)}.`
    : `${heldMeetingCount} meeting${
        heldMeetingCount === 1 ? '' : 's'
      } reviewed for ${activeYear}; no material governance concerns identified.`;

  return (
    <RiskCompliancePanel>
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div
          role="button"
          tabIndex={0}
          aria-label="Board Activity details"
          aria-expanded={detailsOpen}
          onClick={() => setDetailsOpen((open) => !open)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setDetailsOpen((open) => !open);
            }
          }}
          style={{ cursor: 'pointer', outline: 'none' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <SubLabel>Board Activity</SubLabel>
            <ChevronDown
              size={16}
              style={{
                color: COLORS.textDim,
                transform: detailsOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 160ms ease',
              }}
            />
          </div>
          <div style={{ marginTop: detailsOpen ? 14 : 2 }}>
            <ComplianceFindingRow status={status} description={summary} />
          </div>
        </div>
        {detailsOpen && activeYear != null && (
          <div style={{ marginTop: 18, borderTop: `1px solid ${COLORS.border}`, paddingTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <YearFilter years={years} selected={activeYear} onSelect={setSelectedYear} />
              <DavisStirlingInfoTip />
              {isStale && mostRecent && (
                <Tip
                  text={`No board activity on file since ${fmtDate(
                    mostRecent.date,
                  )} — this record may be out of date.`}>
                  <AlertTriangle size={14} style={{ color: COLORS.urgent, display: 'block' }} />
                </Tip>
              )}
            </div>
            <BoardActivityCadenceSummary months={calendarMonths} compact />
            <ScrollHintList
              style={{
                width: isMobile ? '100%' : '50%',
                maxHeight: 220,
                overflowY: 'auto',
                overflowX: 'hidden',
                // no scrollbar: the list's fading last rows say there's more
                scrollbarWidth: 'none',
                marginTop: 14,
              }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {timeline.map((entry, i) => (
                  <BoardTimelineRow
                    key={entry.type === 'meeting' ? entry.meeting.date : entry.dates[0]}
                    entry={entry}
                    isLast={i === timeline.length - 1}
                  />
                ))}
              </div>
            </ScrollHintList>
          </div>
        )}
      </div>
    </RiskCompliancePanel>
  );
}

// Filled dot = at least one meeting held that month, hollow dot = no
// meeting that month — the same held/gap distinction the vertical timeline
// below makes, compressed to one scannable line so the overall cadence
// reads at a glance before scrolling into any one month's detail.
//
// Always exactly 12 months, so the connectors are flex: 1 (elastic) between
// fixed-content dot/label columns — the row always resolves to exactly the
// card's content width with no overflow, rather than a fixed per-item width
// that may or may not add up to the container. The first column aligns
// flex-start (not center, like the rest) so its dot sits flush on the same
// left edge as the card's title and 20px padding, instead of being inset by
// half the label's width.
function BoardActivityCadenceSummary({
  months,
  compact = false,
}: {
  months: MonthlyCadenceDot[];
  compact?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        paddingTop: compact ? 20 : 70,
        paddingBottom: compact ? 4 : 10,
        marginBottom: compact ? 8 : 16,
      }}>
      {months.map((m, i) => (
        <Fragment key={m.key}>
          {i > 0 && (
            <div
              style={{ flex: 1, height: 1, background: COLORS.border, marginTop: 5, minWidth: 4 }}
            />
          )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: i === 0 ? 'flex-start' : 'center',
              flexShrink: 0,
            }}>
            <div
              style={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                boxSizing: 'border-box',
                background: m.held ? COLORS.accent2 : 'transparent',
                border: `1.5px solid ${m.held ? COLORS.accent2 : COLORS.border}`,
                flexShrink: 0,
              }}
            />
            <span
              style={{ fontSize: 10, color: COLORS.textDim, marginTop: 6, whiteSpace: 'nowrap' }}>
              {m.label}
            </span>
          </div>
        </Fragment>
      ))}
    </div>
  );
}

function YearFilter({
  years,
  selected,
  onSelect,
}: {
  years: number[];
  selected: number;
  onSelect: (year: number) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
      {years.map((year, i) => {
        const active = year === selected;
        return (
          <Fragment key={year}>
            {i > 0 && <span style={{ fontSize: 11, color: COLORS.border }}>/</span>}
            <button
              type="button"
              onClick={() => onSelect(year)}
              style={{
                fontSize: 11,
                fontWeight: active ? 600 : 500,
                padding: 0,
                border: 'none',
                background: 'transparent',
                color: active ? COLORS.text : COLORS.textDim,
                cursor: 'pointer',
                fontFamily: "'Manrope', sans-serif",
              }}>
              {year}
            </button>
          </Fragment>
        );
      })}
    </div>
  );
}

function BoardTimelineRow({ entry, isLast }: { entry: BoardTimelineEntry; isLast: boolean }) {
  const isActiveGap =
    entry.type === 'gap' && isLast && entry.dates.length >= GOVERNANCE_CADENCE_GAP_THRESHOLD;
  const dotColor =
    entry.type === 'meeting' ? COLORS.accent2 : isActiveGap ? COLORS.urgent : COLORS.border;
  const hasBody = entry.type === 'meeting' && hasMaterialMeetingContent(entry.meeting);

  return (
    <div>
      {/* Header row — the dot and the label are flex siblings centered
          against each other with align-items: center, so the dot lands on
          the label's true vertical center regardless of the label's own
          font size or padding (a gap's "Ongoing" badge is taller than a
          plain meeting date, and previously threw the dot off-center).
          A meeting entry's bold 12px date sits a bit lower than the dot's
          geometric center due to line-height leading, so its dot gets a
          small `top` nudge to compensate — a gap entry's smaller 11px label
          doesn't have the same offset, so it keeps the plain centered dot. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: dotColor,
            flexShrink: 0,
            position: 'relative',
            top: entry.type === 'meeting' ? 2 : 0,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {entry.type === 'meeting' ? (
            <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>
              {fmtDate(entry.meeting.date)}
            </span>
          ) : (
            <GapHeaderContent dates={entry.dates} active={isActiveGap} />
          )}
        </div>
      </div>
      {/* Connector row — the vertical line sits under the dot (matching
          spacer width), detail content sits under the label. */}
      {(hasBody || !isLast) && (
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ width: 8, display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
            {!isLast && (
              <div style={{ width: 1, flex: 1, background: COLORS.border, marginTop: 2 }} />
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0, paddingTop: 4, paddingBottom: isLast ? 0 : 14 }}>
            {hasBody && entry.type === 'meeting' && <MeetingBodyContent meeting={entry.meeting} />}
          </div>
        </div>
      )}
    </div>
  );
}

function GapHeaderContent({ dates, active }: { dates: string[]; active: boolean }) {
  const rangeLabel =
    dates.length > 1
      ? `${fmtDate(dates[0])} – ${fmtDate(dates[dates.length - 1])}`
      : fmtDate(dates[0]);
  const monthsLabel = dates.length > 1 ? ` (${dates.length} months)` : '';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: active ? '8px 10px' : '2px 0',
        background: active ? 'rgba(255,154,77,0.08)' : 'transparent',
        border: 'none',
        borderRadius: active ? 8 : 0,
      }}>
      <span
        style={{
          fontSize: 12,
          fontWeight: active ? 600 : 500,
          color: active ? COLORS.text : COLORS.textDim,
        }}>
        No meeting held — {rangeLabel}
        {monthsLabel}
      </span>
      {active && <AlertTriangle size={14} style={{ color: COLORS.urgent, flexShrink: 0 }} />}
    </div>
  );
}

function hasMaterialMeetingContent(meeting: HOABoardMeetingEntry): boolean {
  const hasFinancialValues =
    meeting.financials?.operatingBalance != null || meeting.financials?.reserveBalance != null;

  return (
    hasFinancialValues ||
    (meeting.notableApprovals?.length ?? 0) > 0 ||
    (meeting.lienActions?.length ?? 0) > 0 ||
    (meeting.complianceItems?.length ?? 0) > 0 ||
    (meeting.agendaItems?.length ?? 0) > 0
  );
}

// Each meeting's buyer-relevant detail block. Meeting administration such as
// attendance, start/end times, future agendas, and approval of the minutes is
// intentionally kept out of the visible narrative.
// Scrolling is handled once, by the timeline's own ScrollHintList — no
// nested scroll area here.
function MeetingBodyContent({ meeting }: { meeting: HOABoardMeetingEntry }) {
  const hasFinancialValues =
    meeting.financials?.operatingBalance != null || meeting.financials?.reserveBalance != null;

  if (!hasMaterialMeetingContent(meeting)) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {hasFinancialValues && meeting.financials && (
        <span style={{ fontSize: 11, color: COLORS.textDim }}>
          Operating {fmtMoney(meeting.financials.operatingBalance)} · Reserve{' '}
          {fmtMoney(meeting.financials.reserveBalance)}
        </span>
      )}
      {meeting.notableApprovals?.map((a, i) => (
        <span key={i} style={{ fontSize: 11, color: COLORS.textDim }}>
          Approved: {a.description}
          {a.amount != null && ` (${fmtMoney(a.amount)}${a.fundSource ? `, ${a.fundSource}` : ''})`}
        </span>
      ))}
      {meeting.agendaItems?.map((item, i) => {
        const detail = [
          item.discussion,
          item.outcome,
          item.actionItem && `Action: ${item.actionItem}`,
        ]
          .filter(Boolean)
          .join(' — ');
        return (
          <span key={`agenda-${i}`} style={{ fontSize: 11, color: COLORS.textDim }}>
            <strong style={{ color: COLORS.text }}>{item.topic}</strong>
            {detail && `: ${detail}`}
            {item.amount != null && ` (${fmtMoney(item.amount)})`}
          </span>
        );
      })}
      {meeting.lienActions?.map((l, i) => (
        <span key={i} style={{ fontSize: 11, color: COLORS.urgent }}>
          Lien {l.action} — {l.accountRef}
        </span>
      ))}
      {meeting.complianceItems?.map((item, i) => (
        <span key={`compliance-${i}`} style={{ fontSize: 11, color: COLORS.textDim }}>
          Compliance: {item}
        </span>
      ))}
    </div>
  );
}

// ─── Community Rules — fact pills (HOAOperatingRulesFindings) ─────────────

// notableRestrictions is free text extracted from the CC&Rs/rules doc, not
// a controlled category — keyword-match against it (most specific phrases
// first) to pick a relevant icon, same idea as SubscoreDetailCard's
// TRADE_ICON lookup but substring-based since there's no fixed enum here.
// Falls back to the same neutral Info glyph used for tooltip triggers
// elsewhere in the app when nothing matches, rather than guessing.
const BasketballHoop = createLucideIcon('BasketballHoop', [
  ['rect', { x: '5', y: '2', width: '14', height: '8', rx: '1', key: 'backboard' }],
  ['rect', { x: '9', y: '4', width: '6', height: '4', key: 'target-square' }],
  ['path', { d: 'M8 10h8', key: 'rim' }],
  ['path', { d: 'm9 10 2 6h2l2-6', key: 'net' }],
  ['path', { d: 'M10 13h4', key: 'net-crossbar' }],
  ['path', { d: 'M12 16v6', key: 'post' }],
]);

const RESTRICTION_ICON_RULES: Array<{ keywords: string[]; Icon: typeof Info }> = [
  { keywords: ['refrigerator', 'freezer', 'large appliance'], Icon: Refrigerator },
  { keywords: ['air conditioner', 'air conditioning', 'a/c', 'condenser'], Icon: AirVent },
  { keywords: ['basketball', 'sports apparatus', 'sports equipment'], Icon: BasketballHoop },
  { keywords: ['electric vehicle', 'hybrid vehicle', 'ev charging', 'electricity'], Icon: Zap },
  { keywords: ['rv', 'boat', 'trailer', 'camper'], Icon: Caravan },
  { keywords: ['parking'], Icon: Car },
  { keywords: ['solar'], Icon: Sun },
  { keywords: ['paint', 'color'], Icon: Palette },
  { keywords: ['rental', 'lease', 'tenant', 'sublet', 'occupancy'], Icon: Key },
  { keywords: ['pool', 'spa', 'hot tub'], Icon: Waves },
  { keywords: ['noise', 'quiet hours'], Icon: Volume2 },
  {
    keywords: [
      'open-fire',
      'open fire',
      'barbecue',
      'bbq',
      'grill',
      'fire pit',
      'propane',
      'charcoal',
    ],
    Icon: Flame,
  },
  { keywords: ['smoking', 'cigarette', 'tobacco'], Icon: Cigarette },
  { keywords: ['fire', 'gas', 'chimney', 'fireplace'], Icon: Flame },
  { keywords: ['landscap', 'tree', 'plant', 'garden', 'yard', 'lawn'], Icon: Leaf },
  { keywords: ['satellite', 'antenna', 'dish'], Icon: Satellite },
  { keywords: ['sign', 'flag'], Icon: Flag },
  { keywords: ['holiday', 'decoration'], Icon: Sparkles },
  { keywords: ['business', 'commercial'], Icon: Building2 },
  { keywords: ['fenc'], Icon: Fence },
  { keywords: ['bike', 'bicycle'], Icon: Bike },
];

function getRestrictionIcon(text: string): typeof Info {
  const lower = text.toLowerCase();

  // These are broader restrictions that often contain secondary keywords
  // (e.g. "paint colors" or "occupancy"). Resolve the governing subject
  // first, rather than letting a later detail choose the visual treatment.
  if (/\bsolar\b/.test(lower)) return Sun;
  if (/\b(pets?|dogs?|cats?|animals?)\b/.test(lower)) return PawPrint;
  if (
    /\b(exterior|architectural|alterations?|improvements?|board approval|committee approval)\b/.test(
      lower,
    )
  ) {
    return Building2;
  }

  const wholeWordKeywords = new Set(['rv', 'pet', 'dog', 'cat', 'animal']);
  const rule = RESTRICTION_ICON_RULES.find((r) =>
    r.keywords.some((keyword) =>
      wholeWordKeywords.has(keyword)
        ? new RegExp(`\\b${keyword}\\b`, 'i').test(lower)
        : lower.includes(keyword),
    ),
  );
  return rule?.Icon ?? Info;
}

// New Pulse schemas keep this sentence concise. This compatibility shaper
// gives already-processed properties the same presentation immediately by
// removing the legacy adapter's repeated "— threshold; applies to ..." tail.
// The complete stored wording remains available by expanding the row.
export function conciseRestrictionText(value: string): string {
  const trimmed = value.trim();
  const beforeLegacyTail = trimmed.split(/\s+[—–]\s+/)[0]?.trim() || trimmed;
  const firstClause = beforeLegacyTail.split(/;\s+/)[0]?.trim() || beforeLegacyTail;
  let concise = firstClause
    .replace(/\s*\(Sections?[^)]*\)\.?$/i, '')
    .replace(/[.;]\s*$/, '')
    .trim();
  if (concise === trimmed && concise.length <= 160) return trimmed;
  if (concise.length > 160) {
    const shortened = concise
      .slice(0, 157)
      .replace(/\s+\S*$/, '')
      .trimEnd();
    concise = `${shortened}…`;
  }
  return /[.!?…]$/.test(concise) ? concise : `${concise}.`;
}

interface FineScheduleTier {
  amount: string;
  description: string;
}

// Fine schedules extract as one comma-separated sentence — "$100-200 first
// violation, $200-300 second violation of same kind within 12 months, ..."
// Split it back into one row per tier instead of rendering it as a single
// paragraph. Falls back to the raw sentence (null) if any clause doesn't
// match the "$X-Y <description>" shape, rather than mangling a document
// that phrases its fine schedule some other way.
function parseFineSchedule(summary: string): FineScheduleTier[] | null {
  const clauses = summary
    .split(/,\s+(?=\$)/)
    .map((c) => c.trim())
    .filter(Boolean);
  const tiers: FineScheduleTier[] = [];
  for (const clause of clauses) {
    const match = clause.match(/^(\$[\d,]+(?:\s*-\s*\$?[\d,]+)?)\s+(.+)$/);
    if (!match) return null;
    const description = match[2].trim();
    tiers.push({
      amount: match[1].replace(/\s*-\s*/, '–'),
      description: description.charAt(0).toUpperCase() + description.slice(1),
    });
  }
  return tiers.length > 0 ? tiers : null;
}

function CommunityRulesModule({
  findings,
  environmentalFindings,
  documents,
}: {
  findings?: HOAOperatingRulesFindings;
  environmentalFindings?: HOAEnvironmentalFindings;
  documents?: HOAChecklistSource[];
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [fineScheduleOpen, setFineScheduleOpen] = useState(false);
  const [expandedRestrictionIndex, setExpandedRestrictionIndex] = useState<number | null>(null);

  // Current extractions persist Community Rules directly. Pulling CC&R risk
  // findings back into that list double-counted the same provision under two
  // phrasings. Only use environmental findings as a compatibility fallback
  // for legacy properties that have no Community Rules restriction field.
  const usesLegacyCCRRestrictions = findings?.notableRestrictions === undefined;
  const ccrRestrictions = usesLegacyCCRRestrictions
    ? (environmentalFindings?.risks ?? [])
        .filter((risk) =>
          ['use-restriction', 'leasing-restriction', 'architectural-control'].includes(
            risk.category,
          ),
        )
        .map((risk) => risk.shortSummary || risk.title)
        .filter(Boolean)
    : [];
  const displayRestrictions = [...new Set(findings?.notableRestrictions ?? ccrRestrictions)].slice(
    0,
    4,
  );
  const ccrDocumentRecordIds = [
    ...new Set(
      (environmentalFindings?.risks ?? [])
        .filter((risk) =>
          ['use-restriction', 'leasing-restriction', 'architectural-control'].includes(
            risk.category,
          ),
        )
        .map((risk) => risk.documentRecordId)
        .filter((id): id is string => Boolean(id)),
    ),
  ];
  const displayFindings = findings
    ? { ...findings, notableRestrictions: displayRestrictions }
    : displayRestrictions.length > 0
    ? { notableRestrictions: displayRestrictions, documentRecordIds: ccrDocumentRecordIds }
    : undefined;

  if (!displayFindings) {
    return (
      <ModuleCard title="Community rules">
        <EmptyState text="No operating rules processed yet — lease, pet, and lien policy details will appear here once uploaded." />
      </ModuleCard>
    );
  }

  const fineScheduleTiers = displayFindings.fineScheduleSummary
    ? parseFineSchedule(displayFindings.fineScheduleSummary)
    : null;

  // typeof checks, not != null — a stray non-number/non-boolean value (e.g.
  // the literal string "null" instead of an actual null, seen from one
  // extraction) previously slipped past `!= null` and rendered as-is.
  const pills: Array<{ label: string; value: string; tooltip: string; accent?: string }> = [];
  if (displayFindings.ageRestrictionPresent === true) {
    pills.push({
      label: 'Age Requirement',
      value:
        typeof displayFindings.minimumAge === 'number'
          ? `${displayFindings.minimumAge}+`
          : 'Applies',
      tooltip:
        displayFindings.ageRestrictionApplicability ??
        'The governing documents expressly state an age-based occupancy, residency, or use restriction.',
      accent: COLORS.moderate,
    });
  }
  if (typeof displayFindings.minimumLeaseTermDays === 'number') {
    pills.push({
      label: 'Min Lease',
      value: `${displayFindings.minimumLeaseTermDays} days`,
      tooltip: 'Minimum permitted lease/rental term stated in the operating rules.',
    });
  }
  if (typeof displayFindings.smokingProhibited === 'boolean') {
    pills.push({
      label: 'Smoking',
      value: displayFindings.smokingProhibited ? 'Prohibited' : 'Allowed',
      tooltip:
        'Whether smoking is prohibited per the HOA’s rules — may apply to units, common areas, or both depending on the document’s exact wording.',
    });
  }
  if (typeof displayFindings.petLimit === 'number') {
    pills.push({
      label: 'Pet Limit',
      value: String(displayFindings.petLimit),
      tooltip: 'Maximum number of dogs/cats permitted per unit, as stated in the operating rules.',
    });
  }
  if (typeof displayFindings.guestParkingLimitHours === 'number') {
    pills.push({
      label: 'Guest Parking',
      value: `${displayFindings.guestParkingLimitHours} hrs`,
      tooltip: 'Maximum time a guest vehicle may be parked before it violates the rules.',
    });
  }
  if (typeof displayFindings.guestParkingMonthlyLimitHours === 'number') {
    pills.push({
      label: 'Guest Parking / Month',
      value: `${displayFindings.guestParkingMonthlyLimitHours} hrs`,
      tooltip: 'Total guest-parking hours permitted during a month.',
    });
  }
  if (typeof displayFindings.lienThresholdAmount === 'number') {
    pills.push({
      label: 'Lien Threshold',
      value: `${fmtMoney(displayFindings.lienThresholdAmount)}${
        displayFindings.lienThresholdMonths ? ` / ${displayFindings.lienThresholdMonths}mo` : ''
      }`,
      tooltip:
        'Delinquency amount and/or months past due after which the HOA may record a lien or pursue foreclosure.',
    });
  }
  if (typeof displayFindings.lateChargePercent === 'number') {
    pills.push({
      label: 'Late Charge',
      value: `${displayFindings.lateChargePercent}%`,
      tooltip: 'Late fee charged as a percentage of the assessment when a payment is missed.',
    });
  }
  if (typeof displayFindings.interestRatePercent === 'number') {
    pills.push({
      label: 'Interest Rate',
      value: `${displayFindings.interestRatePercent}%`,
      tooltip: 'Annual interest rate charged on delinquent assessment balances.',
    });
  }
  if (typeof displayFindings.vehicleLimit === 'number') {
    pills.push({
      label: 'Vehicle Limit',
      value: String(displayFindings.vehicleLimit),
      tooltip: 'Maximum number of vehicles permitted per lot or unit.',
    });
  }
  if (typeof displayFindings.occupancyLimitPerBedroom === 'number') {
    pills.push({
      label: 'Occupancy',
      value: `${displayFindings.occupancyLimitPerBedroom} / bedroom${
        typeof displayFindings.occupancyAdditionalPersons === 'number'
          ? ` + ${displayFindings.occupancyAdditionalPersons}`
          : ''
      }`,
      tooltip: 'Maximum occupancy stated by the governing documents.',
    });
  }
  if (displayFindings.architecturalApprovalRequired === true) {
    pills.push({
      label: 'Exterior Approval',
      value: 'Required',
      tooltip: 'Exterior or architectural changes require prior HOA approval.',
    });
  }
  if (typeof displayFindings.specialAssessmentPaymentDays === 'number') {
    pills.push({
      label: 'Assessment Due',
      value: `${displayFindings.specialAssessmentPaymentDays} days`,
      tooltip: 'Payment deadline stated for a special individual assessment.',
    });
  }
  if (displayFindings.leaseCopyRequired === true) {
    pills.push({
      label: 'Lease Copy',
      value: 'Required',
      tooltip: 'A copy of the lease must be provided to the HOA.',
    });
  }
  if (displayFindings.garageParkingRequired === true) {
    pills.push({
      label: 'Garage Use',
      value: 'Parking only',
      tooltip: 'The garage must remain available for vehicle parking.',
    });
  }
  if (typeof displayFindings.designReviewFee === 'number') {
    pills.push({
      label: 'Design Review',
      value: fmtMoney(displayFindings.designReviewFee),
      tooltip: 'Fee stated for an architectural or design-review submission.',
    });
  }

  return (
    <ModuleCard
      title="Community rules"
      headerRight={
        <CardCaution
          checks={extractionStalenessChecks([
            {
              label: 'Operating Rules',
              extractedAt: displayFindings.extractedAt,
              monthsThreshold: EXTENDED_STALE_MONTHS_THRESHOLD,
            },
          ])}
        />
      }>
      {pills.length > 0 && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
            gap: 10,
          }}>
          {pills.map((p) => (
            <Stat
              key={p.label}
              label={p.label}
              value={p.value}
              tooltip={p.tooltip}
              accent={p.accent}
              valueFontFamily={VALUE_FONT}
              valueFontSize={18}
            />
          ))}
        </div>
      )}
      {displayFindings.notableRestrictions && displayFindings.notableRestrictions.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <SubLabel>Notable restrictions</SubLabel>
          <ScrollHintList
            style={{
              // This list is deliberately capped at four restrictions above.
              // Four rows should always take their natural height; constraining
              // the window clipped a wrapped fourth item on desktop and made
              // a needless scrollbar despite available card space.
              overflowY: 'visible',
              overflowX: 'hidden',
            }}>
            {displayFindings.notableRestrictions.map((r, i) => {
              const Icon = getRestrictionIcon(r);
              const isLast = i === (displayFindings.notableRestrictions?.length ?? 0) - 1;
              const conciseText = conciseRestrictionText(r);
              const structuredDetail = displayFindings.notableRestrictionDetails?.find(
                (detail) => detail.summary === r,
              );
              const supportingDetails = [
                structuredDetail?.thresholdOrLimit && `Limit: ${structuredDetail.thresholdOrLimit}`,
                structuredDetail?.applicability && `Applies to: ${structuredDetail.applicability}`,
              ].filter((detail): detail is string => Boolean(detail));
              const hasMoreDetail = conciseText !== r.trim() || supportingDetails.length > 0;
              const isExpanded = expandedRestrictionIndex === i;
              return (
                <button
                  type="button"
                  key={i}
                  aria-expanded={hasMoreDetail ? isExpanded : undefined}
                  onClick={
                    hasMoreDetail
                      ? () => setExpandedRestrictionIndex(isExpanded ? null : i)
                      : undefined
                  }
                  style={{
                    display: 'grid',
                    // a light list row: a 16px hairline glyph, the rule, a caret when there's more; no dividers
                    gridTemplateColumns: hasMoreDetail
                      ? '16px minmax(0, 1fr) 14px'
                      : '16px minmax(0, 1fr)',
                    alignItems: 'start',
                    gap: 12,
                    width: '100%',
                    padding: isLast ? '8px 0 0' : '8px 0',
                    borderBottom: 0,
                    borderTop: 0,
                    borderLeft: 0,
                    borderRight: 0,
                    background: 'transparent',
                    color: 'inherit',
                    font: 'inherit',
                    textAlign: 'left',
                    cursor: hasMoreDetail ? 'pointer' : 'default',
                  }}>
                  <Icon size={16} style={{ color: 'var(--tx-muted)', flexShrink: 0, marginTop: 1 }} />
                  <span style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
                    <span
                      style={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                        fontSize: 13,
                        fontWeight: 500,
                        lineHeight: 1.4,
                        color: 'var(--tx-text)',
                        textWrap: 'pretty',
                      }}>
                      {conciseText}
                    </span>
                    {hasMoreDetail && isExpanded && (
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          lineHeight: 1.5,
                          color: 'var(--tx-muted)',
                        }}>
                        {conciseText !== r.trim() && <span>{r}</span>}
                        {supportingDetails.map((detail) => (
                          <span key={detail} style={{ display: 'block' }}>
                            {detail}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                  {hasMoreDetail && (
                    <ChevronDown
                      aria-hidden="true"
                      size={14}
                      style={{
                        marginTop: 2,
                        color: 'var(--tx-faint)',
                        transform: isExpanded ? 'rotate(180deg)' : 'none',
                        transition: 'transform 160ms ease',
                      }}
                    />
                  )}
                </button>
              );
            })}
          </ScrollHintList>
        </div>
      )}
      {displayFindings.fineScheduleSummary && (
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            aria-expanded={fineScheduleOpen}
            onClick={() => setFineScheduleOpen((open) => !open)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              padding: 0,
              border: 0,
              background: 'transparent',
              cursor: 'pointer',
              font: 'inherit',
            }}>
            <SubLabel>Fine schedule</SubLabel>
            <ChevronDown
              size={14}
              style={{
                color: 'var(--tx-faint)',
                transform: fineScheduleOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 160ms ease',
              }}
            />
          </button>
          {fineScheduleOpen &&
            (fineScheduleTiers ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                {fineScheduleTiers.map((tier, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                    {/* the amount as a light figure in its own column, not a tinted pill */}
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 300,
                        letterSpacing: '-0.03em',
                        color: 'var(--tx-text)',
                        fontVariantNumeric: 'tabular-nums',
                        minWidth: 56,
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}>
                      {tier.amount}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: 'var(--tx-muted)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                      {tier.description}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.55, marginTop: 10 }}>
                {displayFindings.fineScheduleSummary}
              </div>
            ))}
        </div>
      )}
      <DocumentCitationRow
        citations={citationsForLogicalDocuments(
          documents,
          ['HOA_OPERATING_RULES', 'HOA_AGE_RESTRICTION', 'HOA_RENTAL_RESTRICTION'],
          citationsForRecordIds(
            displayFindings.documentRecordIds ?? [displayFindings.documentRecordId],
            'Operating Rules',
          ),
        )}
      />
    </ModuleCard>
  );
}

// ─── Governance Documents — California disclosure taxonomy ───────────────
// Group by legal role rather than forcing conditional, requested, and
// supplemental material into one misleading required-document denominator.
// Mirrors HOA_CHECKLIST_ITEMS_BY_CLASSIFICATION on the backend
// (packages/functions/common/models/HOAFindings.ts) — which checklist item
// a given extracted findings object satisfies — but inverted, so a received
// item can look up its own source document's documentRecordId and become
// clickable. The 3 receipt-only items (bylaws, articles-of-incorporation,
// financial-statement) are marked RECEIVED with no extraction at all, so
// there's no findings object — and no document link — for those yet.
const CHECKLIST_ITEM_FINDINGS_SOURCE: Record<string, keyof HOAChecklistResponse> = {
  'demand-statement': 'hoaFindings',
  'litigation-disclosure': 'hoaFindings',
  'violation-notices': 'hoaFindings',
  'board-minutes': 'hoaBoardMinutesFindings',
  'rules-regulations': 'hoaOperatingRulesFindings',
  'assessment-enforcement-notice': 'hoaOperatingRulesFindings',
  'operating-budget': 'hoaAnnualBudgetFindings',
  'reserve-study': 'hoaAnnualBudgetFindings',
  'assessment-reserve-disclosure': 'hoaAnnualBudgetFindings',
  'insurance-summary': 'hoaAnnualBudgetFindings',
  ccrs: 'hoaEnvironmentalFindings',
  'environmental-report': 'hoaEnvironmentalFindings',
};

function buildChecklistDocumentRecordIds(
  data: HOAChecklistResponse,
): Record<string, string | undefined> {
  const result: Record<string, string | undefined> = {};
  for (const item of data.hoaChecklist) {
    if (item.documentRecordId) result[item.id] = item.documentRecordId;
  }
  for (const [itemId, sourceKey] of Object.entries(CHECKLIST_ITEM_FINDINGS_SOURCE)) {
    const findings = data[sourceKey] as { documentRecordId?: string } | undefined;
    result[itemId] ??= findings?.documentRecordId;
  }
  return result;
}

const DISCLOSURE_TIER_GROUPS: Array<{
  key: HOADisclosureTier;
  label: string;
  icon: React.ReactNode;
}> = [
  {
    key: 'STATUTORY_REQUIRED',
    label: 'Required',
    icon: <Scroll size={16} />,
  },
  {
    key: 'STATUTORY_CONDITIONAL',
    label: 'Conditional',
    icon: <Gavel size={16} />,
  },
  {
    key: 'BUYER_REQUESTED',
    label: 'On request',
    icon: <FileText size={16} />,
  },
  {
    key: 'SUPPLEMENTAL',
    label: 'Supplemental',
    icon: <Wallet size={16} />,
  },
];

function GovernanceDocumentsModule({
  items,
  financialReviewGap,
  extractionSources,
  documentRecordIds,
}: {
  items: ChecklistItem[];
  financialReviewGap?: boolean;
  extractionSources: Array<{ label: string; extractedAt?: string; monthsThreshold?: number }>;
  documentRecordIds: Record<string, string | undefined>;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const onFileCount = items.filter((c) => c.status === 'RECEIVED').length;
  const categorized = DISCLOSURE_TIER_GROUPS.map((cat) => ({
    ...cat,
    items: items.filter((item) => getHOADisclosureTier(item) === cat.key),
  })).filter((cat) => cat.items.length > 0);

  return (
    <ModuleCard
      title="Governance documents"
      headerBottomSpacing={10}
      headerExpanded={isOpen}
      headerToggleLabel={isOpen ? 'Hide documents' : 'Show documents'}
      onHeaderToggle={() => setIsOpen((open) => !open)}
      headerRight={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CardCaution checks={extractionStalenessChecks(extractionSources)} />
          <DavisStirlingInfoTip />
          <span
            aria-hidden="true"
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: 0,
              color: COLORS.textDim,
            }}>
            <ChevronDown
              size={16}
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'none',
                transition: 'transform 160ms ease',
              }}
            />
          </span>
        </div>
      }>
      {financialReviewGap && (
        <ListRow
          primary="Independent financial review required (Civil Code §5305) but no statement on file"
          secondary="Gross income exceeds the $75,000 threshold per the Annual Budget Report"
          trailing="Required"
          tone="critical"
        />
      )}
      <div
        style={{
          fontSize: 12,
          color: COLORS.textDim,
          lineHeight: 1.45,
          marginTop: financialReviewGap ? 12 : 0,
        }}>
        <span style={{ color: COLORS.text }}>
          {onFileCount} disclosure {onFileCount === 1 ? 'category' : 'categories'} on file
        </span>
        {' · '}Conditional documents apply only when relevant; approved board minutes are provided
        on request.
      </div>
      {/* PROTOTYPE: each disclosure tier as a folder: received documents peek out as sheets, missing ones are empty
          dashed sleeves, so received-versus-missing reads at a glance */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 20,
          marginTop: 16,
        }}>
        {categorized.map((cat) => {
          const received = cat.items.filter((item) => item.status === 'RECEIVED');
          // a missing document always shows (one empty sleeve at the back), then up to two received sheets
          const missing = cat.items.filter((item) => item.status !== 'RECEIVED');
          const ordered = [...missing.slice(0, 1), ...received.slice(0, missing.length ? 2 : 3)];
          return (
            <DocFolder
              key={cat.key}
              sheets={ordered.map((item) => ({
                title: item.label,
                meta: item.status === 'RECEIVED' ? 'On file' : 'Missing',
                missing: item.status !== 'RECEIVED',
              }))}
              label={cat.label}
              sublabel={missing.length ? `${missing.length} missing` : 'All on file'}
              count={`${received.length} of ${cat.items.length}`}
            />
          );
        })}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 20,
          // Grid items stretch to match the row's tallest column by default —
          // categories have different item counts, so each sizes to its own
          // content instead of stretching to match its tallest sibling.
          alignItems: 'start',
          marginTop: 10,
        }}>
        {categorized.map((cat) => (
          <CategoryGroup
            key={cat.key}
            label={cat.label}
            icon={cat.icon}
            items={cat.items}
            documentRecordIds={documentRecordIds}
            isOpen={isOpen}
          />
        ))}
      </div>
    </ModuleCard>
  );
}

function CategoryGroup({
  label,
  icon,
  items,
  documentRecordIds,
  isOpen,
}: {
  label: string;
  icon: React.ReactNode;
  items: ChecklistItem[];
  documentRecordIds: Record<string, string | undefined>;
  /** Card-wide expand state, owned by GovernanceDocumentsModule — all
   *  category groups show/hide their document lists together, not per-group. */
  isOpen: boolean;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const receivedCount = items.filter((i) => i.status === 'RECEIVED').length;
  const isIncomplete = receivedCount < items.length;
  const { open, openingId } = useOpenDocumentRecord();
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: isOpen ? (isMobile ? 18 : 13) : 0,
        }}>
        <span style={{ color: COLORS.accent2, display: 'flex', flexShrink: 0 }}>{icon}</span>
        <span style={HOA_SECTION_HEADING_STYLE}>{label}</span>
        <span
          style={{
            fontSize: 12,
            fontWeight: isIncomplete ? 600 : 400,
            color: isIncomplete ? COLORS.urgent : COLORS.textDim,
            opacity: isIncomplete ? 1 : 0.7,
          }}>
          ({receivedCount}/{items.length})
        </span>
      </div>
      {isOpen && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {items.map((item) => {
            const primarySource = item.sources?.[0];
            const documentRecordId = primarySource?.documentRecordId ?? documentRecordIds[item.id];
            const clickable = item.status === 'RECEIVED' && !!documentRecordId;
            const openingKey = primarySource?.manifestItemId ?? documentRecordId;
            const loading = clickable && openingId === openingKey;
            // Flat list, no per-item box or divider at rest — with up to 5
            // items per category across 4 categories, the repeated boxes read
            // as busier than the rest of the page. Received/unreceived is
            // signaled only by the checkmark instead of dimming document
            // titles. Clickable rows still get the original boxed
            // treatment back on hover (same tint/border/radius), so there's
            // still a clear "this opens something" affordance despite the
            // flat resting state. Padding stays 12px horizontal at rest (not
            // just 0) so hovering doesn't shift the label — only the border/
            // background appear/disappear.
            const restBoxStyle: React.CSSProperties = {
              border: 0,
              borderRadius: 8,
              background: 'transparent',
            };
            const hoverBoxStyle: React.CSSProperties = {
              border: 0,
              borderRadius: 8,
              background: 'var(--tx-fill)',
            };
            const hoverHandlers = clickable
              ? {
                  onMouseEnter: (e: React.MouseEvent<HTMLButtonElement>) => {
                    Object.assign(e.currentTarget.style, hoverBoxStyle);
                  },
                  onMouseLeave: (e: React.MouseEvent<HTMLButtonElement>) => {
                    Object.assign(e.currentTarget.style, restBoxStyle);
                  },
                }
              : {};
            const rowStyle: React.CSSProperties = {
              padding: '9px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              width: '100%',
              font: 'inherit',
              textAlign: 'left',
              cursor: clickable ? 'pointer' : 'default',
              opacity: loading ? 0.6 : 1,
              ...restBoxStyle,
            };
            const labelStyle: React.CSSProperties = {
              fontSize: isMobile ? 11 : 12,
              color: COLORS.text,
              flex: 1,
              minWidth: 0,
            };
            // The card-level info treatment establishes the California
            // statutory basis once. Repeating §4525/§5300 beneath nearly
            // every row makes the checklist harder to scan without changing
            // the buyer's decision. Keep the plain-English applicability
            // note here; genuinely actionable exceptions (for example, the
            // §5305 financial-review gap above) retain their citation where
            // the code changes the conclusion.
            const detail = item.applicabilityNote;
            const itemContents = (
              <>
                <span style={labelStyle}>{loading ? 'Opening…' : item.label}</span>
                {item.status === 'RECEIVED' && <HOAReceivedIcon />}
              </>
            );
            return clickable ? (
              <div key={item.id}>
                <button
                  type="button"
                  onClick={() =>
                    open(
                      documentRecordId,
                      primarySource?.primaryPage,
                      primarySource?.manifestItemId,
                    )
                  }
                  disabled={loading}
                  style={rowStyle}
                  {...hoverHandlers}>
                  {itemContents}
                </button>
                {detail && <ChecklistDisclosureDetail detail={detail} />}
              </div>
            ) : (
              <div key={item.id}>
                <div style={rowStyle}>{itemContents}</div>
                {detail && <ChecklistDisclosureDetail detail={detail} />}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ChecklistDisclosureDetail({ detail }: { detail: string }) {
  return (
    <div
      style={{
        color: COLORS.textDim,
        fontSize: 10,
        lineHeight: 1.4,
        margin: '-4px 12px 7px',
      }}>
      {detail}
    </div>
  );
}

// ─── Shared small pieces ───────────────────────────────────────────────────

function SubLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        ...HOA_SECTION_HEADING_STYLE,
        marginBottom: 6,
      }}>
      {children}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div style={{ fontSize: 13, color: COLORS.textDim, fontStyle: 'italic', lineHeight: 1.55 }}>
      {text}
    </div>
  );
}

function Stat({
  label,
  value,
  detail,
  detailPrefix = '/',
  tone,
  tooltip,
  accent,
  valueFontSize = 14,
  valueFontFamily,
  stacked = false,
}: {
  label: string;
  value: string;
  detail?: string;
  detailPrefix?: string;
  tone?: string;
  tooltip?: string;
  accent?: string;
  /** phone layout: figure in ink, the tone as a tick before the label, the detail on its own line beneath */
  stacked?: boolean;
  /** Override the value's font size — defaults to the compact 14px pill size. */
  valueFontSize?: number;
  /** Override the value's font family — e.g. VALUE_FONT for a hero-style stat card. */
  valueFontFamily?: string;
}) {
  // a stat in the dashboards' grammar: the light figure first, its label beneath, no box (an accent tints the figure)
  const content = stacked ? (
    <div style={{ padding: '4px 0', cursor: tooltip ? 'help' : 'default', minWidth: 0 }}>
      <div
        style={{
          fontFamily: valueFontFamily,
          fontSize: Math.max(valueFontSize, 18),
          fontWeight: 300,
          letterSpacing: '-0.03em',
          lineHeight: 1.15,
          color: 'var(--tx-text)',
          fontVariantNumeric: 'tabular-nums',
        }}>
        {value}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 6 }}>
        {(tone ?? accent) && <span style={{ width: 2, height: 12, borderRadius: 1, background: tone ?? accent, flexShrink: 0 }} />}
        {label}
      </div>
      {detail && (
        <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)', marginTop: 2 }}>
          {detail.charAt(0).toUpperCase() + detail.slice(1)}
        </div>
      )}
    </div>
  ) : (
    <div style={{ padding: '4px 0', cursor: tooltip ? 'help' : 'default', minWidth: 0 }}>
      <div
        style={{
          fontFamily: valueFontFamily,
          fontSize: Math.max(valueFontSize, 18),
          fontWeight: 300,
          letterSpacing: '-0.03em',
          lineHeight: 1.15,
          color: tone ?? accent ?? 'var(--tx-text)',
          fontVariantNumeric: 'tabular-nums',
        }}>
        {value}
        {detail && (
          <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: 0, color: 'var(--tx-muted)', marginLeft: 4 }}>
            {detailPrefix}
            {detail}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', marginTop: 4 }}>{label}</div>
    </div>
  );
  return tooltip ? (
    <Tip text={tooltip} style={{ display: 'block', borderBottom: 'none' }}>
      {content}
    </Tip>
  ) : (
    content
  );
}

function ListSubsection<T>({
  label,
  items,
  empty,
  renderItem,
  gap = 6,
  splitContent = false,
}: {
  label: string;
  items: T[];
  empty: string;
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Space between items. Defaults to 6 (the card-row look most lists use).
   *  Pass 0 for divider-separated lists, where each row's own padding
   *  already controls spacing — otherwise this gap stacks on top of that
   *  padding and throws off the divider's vertical centering. */
  gap?: number;
  splitContent?: boolean;
}) {
  return (
    <div
      style={{
        display: splitContent ? 'flex' : undefined,
        flexDirection: splitContent ? 'column' : undefined,
        flex: splitContent ? 1 : undefined,
        height: splitContent ? '100%' : undefined,
      }}>
      <SubLabel>{label}</SubLabel>
      {items.length === 0 ? (
        <EmptyState text={empty} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap }}>{items.map(renderItem)}</div>
      )}
    </div>
  );
}

// Two severity tones, not one: `critical` (pink) is reserved for things
// already legally/financially in effect — an open violation, a required
// disclosure that's missing. `urgent` (orange) is for risk signals worth a
// buyer's attention but not yet a compliance failure — a coverage gap, a
// governance lapse. Mixing these into one color made every flagged row read
// as equally alarming; splitting them lets the eye triage at a glance.
type ListRowTone = 'critical' | 'urgent';

const TONE_STYLES: Record<ListRowTone, { bg: string; border: string; color: string }> = {
  critical: { bg: 'rgba(219,82,130,0.08)', border: 'rgba(219,82,130,0.3)', color: COLORS.critical },
  urgent: { bg: 'rgba(255,154,77,0.08)', border: 'rgba(255,154,77,0.3)', color: COLORS.urgent },
};

function ListRow({
  primary,
  secondary,
  trailing,
  flagged,
  tone,
}: {
  primary: string;
  secondary?: string;
  trailing?: string;
  flagged?: boolean;
  tone?: ListRowTone;
}) {
  const activeTone = tone ?? (flagged ? 'critical' : undefined);
  const style = activeTone ? TONE_STYLES[activeTone] : undefined;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        padding: '8px 12px',
        background: style?.bg ?? 'var(--tx-glass-panel)',
        border: 0,
        borderRadius: 8,
      }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: COLORS.text }}>{primary}</div>
        {secondary && (
          <div style={{ fontSize: 11, color: COLORS.textDim, marginTop: 2 }}>{secondary}</div>
        )}
      </div>
      {trailing && (
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: style?.color ?? COLORS.textDim,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
          {trailing}
        </span>
      )}
    </div>
  );
}

export default HOADetailView;
